import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import QRCode from "react-qr-code";
import AppShell from "../layouts/AppShell";
import { GET, POST, PUT, DELETE, loading, alert, Select, copyText, profile, DateInput } from "../components/CustomComponent";

// ชั้นเงาแบบ 2 ชั้น (เส้นคมบาง + เงานุ่มลึก) ให้การ์ดดูลอยแบบพรีเมียมโดยไม่ทึบ
const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(15, 60, 120, 0.07)",
  borderRadius: 22,
  boxShadow: "0 1px 2px rgba(16, 46, 82, 0.04), 0 22px 48px -26px rgba(11, 79, 132, 0.40)",
};

const modalLabelStyle = {
  color: "#5b7c96",
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  marginBottom: 6,
};

const modalHeaderStyle = {
  background:
    "radial-gradient(circle at 88% -30%, rgba(47, 213, 228, 0.34), transparent 46%), linear-gradient(120deg, #0f3a63 0%, #12558f 52%, #0c76e5 100%)",
};

const monoStyle = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.82rem" };

// สีของ environment ตั้งใจให้ production เด่นสุด จะได้ไม่เผลอแก้ผิดตัว
const ENV_TYPES = [
  { value: "production", label: "Production", short: "PROD", color: "#c0392b", background: "rgba(192, 57, 43, 0.12)" },
  { value: "sandbox", label: "Sandbox", short: "SANDBOX", color: "#b97007", background: "rgba(245, 166, 35, 0.16)" },
  { value: "staging", label: "Staging", short: "STAGING", color: "#6b4bb5", background: "rgba(107, 75, 181, 0.14)" },
  { value: "develop", label: "Develop", short: "DEV", color: "#0c76e5", background: "rgba(12, 117, 230, 0.14)" },
  { value: "local", label: "Local", short: "LOCAL", color: "#566575", background: "rgba(86, 101, 117, 0.14)" },
];

const APP_TYPES = [
  { value: "backend", label: "Backend / API", icon: "fa-server", color: "#188251" },
  { value: "web", label: "Web (Frontend)", icon: "fa-window-maximize", color: "#0c76e5" },
  { value: "mobile", label: "Mobile", icon: "fa-mobile-screen", color: "#6b4bb5" },
  { value: "desktop", label: "Desktop", icon: "fa-desktop", color: "#0b4f84" },
  { value: "service", label: "Service / Worker", icon: "fa-gears", color: "#b97007" },
  { value: "other", label: "อื่น ๆ", icon: "fa-cube", color: "#566575" },
];

const PROJECT_STATUS = [
  { value: "active", label: "กำลังพัฒนา / ใช้งาน", color: "#188251", background: "rgba(47, 181, 114, 0.14)" },
  { value: "maintenance", label: "ดูแลรักษา (MA)", color: "#0c76e5", background: "rgba(12, 117, 230, 0.14)" },
  { value: "hold", label: "พักงาน", color: "#b97007", background: "rgba(245, 166, 35, 0.16)" },
  { value: "closed", label: "ปิดโปรเจค", color: "#566575", background: "rgba(86, 101, 117, 0.14)" },
];

const PROJECT_TYPES = [
  { value: "client", label: "งานลูกค้า" },
  { value: "internal", label: "ระบบภายใน" },
  { value: "product", label: "ผลิตภัณฑ์ของบริษัท" },
];


const envMeta = (value) => ENV_TYPES.find((item) => item.value === value) || ENV_TYPES[3];
const appMeta = (value) => APP_TYPES.find((item) => item.value === value) || APP_TYPES[5];
const statusMeta = (value) => PROJECT_STATUS.find((item) => item.value === value) || PROJECT_STATUS[0];

const appPermissions = profile?.role?.permission || [];
// ค่า ENV เป็นความลับระดับที่หลุดแล้วเข้าระบบลูกค้าได้ทั้งชุด จึงล็อกไว้ที่ super_user เท่านั้น
const isSuperUser = profile?.user_type === "super_user";
// ปุ่มเพิ่ม/แก้ไข/ลบทั้งหน้านี้เปิดให้เฉพาะ super_user คนอื่นดูได้อย่างเดียว
// ถึงจะมี permission ในตารางก็ไม่พอ เพราะทะเบียนโปรเจคผูกกับปลายทาง/ENV ของลูกค้าจริง
const can = (key) => isSuperUser && appPermissions.includes(key);

const normalizeUrl = (url) => {
  const value = String(url || "").trim();
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" });
};

// เตือนล่วงหน้า 30 วันก่อนโดเมน/SSL หมดอายุ เพราะกว่าจะต่อได้ใช้เวลาหลายวัน
const expireState = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const days = Math.ceil((date.getTime() - Date.now()) / 86400000);
  if (days < 0) return { days, tone: "#c0392b", text: "หมดอายุแล้ว" };
  if (days <= 30) return { days, tone: "#b97007", text: `อีก ${days} วัน` };
  return { days, tone: "#5b7488", text: formatDate(value) };
};

const CopyButton = ({ value, title }) => (
  <button
    type="button"
    className="btn btn-sm btn-link p-0 ms-2 align-baseline"
    title={title || "คัดลอก"}
    style={{ color: "#0c76e5", textDecoration: "none" }}
    onClick={() => copyText(value)}
  >
    <i className="fa-regular fa-copy" />
  </button>
);

// ลิงก์เปิดหน้าเว็บของ environment กดแล้วเปิดแท็บใหม่ได้ทันที
const ExternalLink = ({ url, label }) => {
  const href = normalizeUrl(url);
  if (!href) return <span style={{ color: "#a3b6c6" }}>-</span>;

  return (
    <span className="d-inline-flex align-items-center" style={{ maxWidth: "100%" }}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-truncate"
        style={{ color: "#0c76e5", fontWeight: 600, maxWidth: 260 }}
        title={href}
      >
        {label || url} <i className="fa-solid fa-arrow-up-right-from-square ms-1" style={{ fontSize: "0.7rem" }} />
      </a>
      <CopyButton value={href} />
    </span>
  );
};

const projectFormDefaults = {
  uid: "",
  project_code: "",
  name: "",
  client_name: "",
  project_type: "client",
  status: "active",
  owner: "",
  start_date: "",
  go_live_date: "",
  doc_url: "",
  board_url: "",
  description: "",
  note: "",
};

const ProjectModal = ({ show, onHide, editData, onSave }) => {
  const [formData, setFormData] = useState(projectFormDefaults);

  useEffect(() => {
    setFormData({
      ...projectFormDefaults,
      uid: editData?.uid || "",
      project_code: editData?.project_code || "",
      name: editData?.name || "",
      client_name: editData?.client_name || "",
      project_type: editData?.project_type || "client",
      status: editData?.status || "active",
      owner: editData?.owner || "",
      start_date: editData?.start_date || "",
      go_live_date: editData?.go_live_date || "",
      doc_url: editData?.doc_url || "",
      board_url: editData?.board_url || "",
      description: editData?.description || "",
      note: editData?.note || "",
    });
  }, [editData, show]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const setField = (name, value) => setFormData((current) => ({ ...current, [name]: value }));

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="pj-modal border-0 overflow-hidden" dialogClassName="modal-dialog-scrollable">
      <Modal.Header closeButton closeVariant="white" className="border-0 px-4 py-4" style={modalHeaderStyle}>
        <div className="d-flex align-items-center gap-3">
          <span className="pj-modal-icon"><i className="fa-solid fa-diagram-project" /></span>
          <div>
            <div className="pj-eyebrow" style={{ color: "rgba(226, 243, 255, 0.70)" }}>ทะเบียนโปรเจค</div>
            <Modal.Title className="fw-bold text-white mb-0" style={{ fontSize: "1.3rem", letterSpacing: "-0.01em" }}>
              {editData ? "แก้ไขโปรเจค" : "เพิ่มโปรเจค"}
            </Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-4" style={{ background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)" }}>
        <div className="pj-form-section">ข้อมูลโปรเจค</div>
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label" style={modalLabelStyle}>รหัสย่อ</label>
            <input type="text" name="project_code" className="form-control pj-input text-uppercase" value={formData.project_code} onChange={handleChange} placeholder="BPS" />
          </div>
          <div className="col-md-9">
            <label className="form-label" style={modalLabelStyle}>ชื่อโปรเจค</label>
            <input type="text" name="name" className="form-control pj-input" value={formData.name} onChange={handleChange} placeholder="ระบบบริหารงานซ่อมบำรุง" />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ลูกค้า</label>
            <input type="text" name="client_name" className="form-control pj-input" value={formData.client_name} onChange={handleChange} placeholder="ชื่อบริษัทลูกค้า" />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ผู้ดูแลหลัก</label>
            <input type="text" name="owner" className="form-control pj-input" value={formData.owner} onChange={handleChange} placeholder="ชื่อผู้รับผิดชอบ" />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ประเภทงาน</label>
            <Select className="form-control pj-input" value={formData.project_type} options={PROJECT_TYPES} default={false} placeholder="เลือกประเภทงาน" onChange={(value) => setField("project_type", value || "client")} />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>สถานะ</label>
            <Select className="form-control pj-input" value={formData.status} options={PROJECT_STATUS.map(({ value, label }) => ({ value, label }))} default={false} placeholder="เลือกสถานะ" onChange={(value) => setField("status", value || "active")} />
          </div>
        </div>

        <div className="pj-form-section">ช่วงเวลา</div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>วันเริ่มโปรเจค</label>
            <DateInput name="start_date" className="form-control pj-input" value={formData.start_date} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>วันขึ้น Production</label>
            <DateInput name="go_live_date" className="form-control pj-input" value={formData.go_live_date} onChange={handleChange} />
          </div>
        </div>

        <div className="pj-form-section">ลิงก์และรายละเอียด</div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ลิงก์เอกสาร / สเปค</label>
            <input type="text" name="doc_url" className="form-control pj-input" value={formData.doc_url} onChange={handleChange} placeholder="https://..." />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ลิงก์บอร์ดงาน</label>
            <input type="text" name="board_url" className="form-control pj-input" value={formData.board_url} onChange={handleChange} placeholder="https://trello.com/..." />
          </div>
          <div className="col-12">
            <label className="form-label" style={modalLabelStyle}>รายละเอียด</label>
            <textarea name="description" className="form-control pj-input" rows={2} value={formData.description} onChange={handleChange} placeholder="ขอบเขตงานโดยย่อ" />
          </div>
          <div className="col-12">
            <label className="form-label" style={modalLabelStyle}>หมายเหตุ</label>
            <textarea name="note" className="form-control pj-input" rows={2} value={formData.note} onChange={handleChange} />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 py-3" style={{ background: "#f7fbff", borderTop: "1px solid #e8f1f9" }}>
        <div className="d-flex flex-column flex-sm-row gap-2 w-100 justify-content-end">
          <button type="button" className="pj-btn pj-btn-ghost" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="pj-btn pj-btn-primary" onClick={() => onSave(formData)}>
            <i className="fa-solid fa-check me-2" />บันทึกข้อมูล
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const appFormDefaults = {
  uid: "",
  project_uid: "",
  name: "",
  app_type: "web",
  tech_stack: "",
  git_url: "",
  git_provider: "github",
  default_branch: "main",
  package_id: "",
  store_url: "",
  sort_order: "0",
  note: "",
};

let envRowSeq = 0;
// id ชั่วคราวไว้เป็น key ของ React เท่านั้น ไม่ส่งขึ้น API — แถวใหม่ยังไม่มี uid จากเซิร์ฟเวอร์
const newEnvRow = () => ({ rowId: `new-${(envRowSeq += 1)}`, uid: "", env_type: "production", name: "", server_ips: [""] });

const AppModal = ({ show, onHide, editData, projectUid, projectName, onSave }) => {
  const [formData, setFormData] = useState(appFormDefaults);
  const [environments, setEnvironments] = useState([]);

  useEffect(() => {
    setEnvironments(
      (editData?.environments || []).map((item) => ({
        rowId: item.uid,
        uid: item.uid,
        env_type: item.env_type || "production",
        name: item.name || "",
        // IP ของ server แยกตาม environment ถ้ายังไม่มีให้ขึ้นช่องว่าง 1 ช่องพร้อมกรอก
        server_ips: item.server_ips?.length ? [...item.server_ips] : [""],
      }))
    );
  }, [editData, show]);

  useEffect(() => {
    setFormData({
      ...appFormDefaults,
      uid: editData?.uid || "",
      project_uid: projectUid || "",
      name: editData?.name || "",
      app_type: editData?.app_type || "web",
      tech_stack: editData?.tech_stack || "",
      git_url: editData?.git_url || "",
      git_provider: editData?.git_provider || "github",
      default_branch: editData?.default_branch || "main",
      package_id: editData?.package_id || "",
      store_url: editData?.store_url || "",
      sort_order: String(editData?.sort_order ?? 0),
      note: editData?.note || "",
    });
  }, [editData, projectUid, show]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const setField = (name, value) => setFormData((current) => ({ ...current, [name]: value }));

  const setEnvField = (rowId, key, value) =>
    setEnvironments((current) => current.map((row) => (row.rowId === rowId ? { ...row, [key]: value } : row)));

  const removeEnvRow = (rowId) => setEnvironments((current) => current.filter((row) => row.rowId !== rowId));

  // IP ของ server ต่อ 1 environment เพิ่ม/ลบได้อิสระ ลบจนหมดจะเหลือช่องว่าง 1 ช่องไว้ให้กรอกต่อ
  const setEnvIps = (rowId, updater) =>
    setEnvironments((current) => current.map((row) => (row.rowId === rowId ? { ...row, server_ips: updater(row.server_ips || [""]) } : row)));
  const setEnvIp = (rowId, index, value) => setEnvIps(rowId, (ips) => ips.map((ip, i) => (i === index ? value : ip)));
  const addEnvIp = (rowId) => setEnvIps(rowId, (ips) => [...ips, ""]);
  const removeEnvIp = (rowId, index) =>
    setEnvIps(rowId, (ips) => {
      const next = ips.filter((_, i) => i !== index);
      return next.length ? next : [""];
    });

  const isMobile = formData.app_type === "mobile";

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="pj-modal border-0 overflow-hidden" dialogClassName="modal-dialog-scrollable">
      <Modal.Header closeButton closeVariant="white" className="border-0 px-4 py-4" style={modalHeaderStyle}>
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
          <span className="pj-modal-icon"><i className="fa-solid fa-layer-group" /></span>
          <div style={{ minWidth: 0 }}>
            <div className="pj-eyebrow text-truncate" style={{ color: "rgba(226, 243, 255, 0.70)", maxWidth: 340 }}>{projectName}</div>
            <Modal.Title className="fw-bold text-white mb-0" style={{ fontSize: "1.3rem", letterSpacing: "-0.01em" }}>
              {editData ? "แก้ไขแอป" : "เพิ่มแอปในโปรเจค"}
            </Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-4" style={{ background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)" }}>
        <div className="pj-form-section">ข้อมูลแอป</div>
        <div className="row g-3">
          <div className="col-md-7">
            <label className="form-label" style={modalLabelStyle}>ชื่อแอป</label>
            <input type="text" name="name" className="form-control pj-input" value={formData.name} onChange={handleChange} placeholder="Web Admin / Backend API" />
          </div>
          <div className="col-md-5">
            <label className="form-label" style={modalLabelStyle}>ประเภท</label>
            <Select className="form-control pj-input" value={formData.app_type} options={APP_TYPES.map(({ value, label }) => ({ value, label }))} default={false} placeholder="เลือกประเภท" onChange={(value) => setField("app_type", value || "web")} />
          </div>
          <div className="col-md-7">
            <label className="form-label" style={modalLabelStyle}>เทคโนโลยี</label>
            <input type="text" name="tech_stack" className="form-control pj-input" value={formData.tech_stack} onChange={handleChange} placeholder="React 18 + Bootstrap" />
          </div>
          <div className="col-md-5">
            <label className="form-label" style={modalLabelStyle}>ลำดับแสดงผล</label>
            <input type="number" name="sort_order" className="form-control pj-input" value={formData.sort_order} onChange={handleChange} min="0" />
          </div>
        </div>

        <div className="pj-form-section">ซอร์สโค้ด</div>
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label" style={modalLabelStyle}>Git URL</label>
            <input type="text" name="git_url" className="form-control pj-input" value={formData.git_url} onChange={handleChange} placeholder="https://github.com/org/repo" />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>Git Provider</label>
            <input type="text" name="git_provider" className="form-control pj-input" value={formData.git_provider} onChange={handleChange} placeholder="github / gitlab" />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>Branch หลัก</label>
            <input type="text" name="default_branch" className="form-control pj-input" value={formData.default_branch} onChange={handleChange} placeholder="main" />
          </div>
        </div>

        {isMobile ? (
          <>
            <div className="pj-form-section">ข้อมูลสโตร์</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" style={modalLabelStyle}>Bundle / Application ID</label>
                <input type="text" name="package_id" className="form-control pj-input" value={formData.package_id} onChange={handleChange} placeholder="com.company.app" />
              </div>
              <div className="col-md-6">
                <label className="form-label" style={modalLabelStyle}>ลิงก์สโตร์</label>
                <input type="text" name="store_url" className="form-control pj-input" value={formData.store_url} onChange={handleChange} placeholder="https://play.google.com/..." />
              </div>
            </div>
          </>
        ) : null}

        <div className="pj-form-section">Environment</div>
        <div className="env-rows">
          {environments.length ? (
            environments.map((row) => {
              const ips = row.server_ips?.length ? row.server_ips : [""];
              const rowMeta = envMeta(row.env_type);

              return (
                <div className="env-block" key={row.rowId} style={{ "--env-color": rowMeta.color }}>
                  <div className="env-row">
                    <div className="env-row-type">
                      <Select
                        className="form-control pj-input"
                        value={row.env_type}
                        options={ENV_TYPES.map(({ value, label }) => ({ value, label }))}
                        default={false}
                        placeholder="เลือกประเภท"
                        onChange={(value) => setEnvField(row.rowId, "env_type", value || "production")}
                      />
                    </div>
                    <input
                      type="text"
                      className="form-control pj-input flex-grow-1"
                      value={row.name}
                      placeholder="ชื่อกำกับ เช่น PROD - AWS Singapore (ไม่บังคับ)"
                      onChange={(event) => setEnvField(row.rowId, "name", event.target.value)}
                    />
                    <button type="button" className="pj-icon-btn pj-icon-btn-danger flex-shrink-0" title="ลบ environment นี้" onClick={() => removeEnvRow(row.rowId)}>
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>

                  <div className="env-block-ips">
                    <span className="env-block-ips-label">
                      <i className="fa-solid fa-server" />
                      IP Server
                    </span>
                    <div className="env-block-ips-list">
                      {ips.map((ip, index) => (
                        <div className="env-block-ip-row" key={index}>
                          <input
                            type="text"
                            className="form-control pj-input flex-grow-1"
                            value={ip}
                            placeholder="เช่น 10.0.0.1"
                            onChange={(event) => setEnvIp(row.rowId, index, event.target.value)}
                          />
                          <button
                            type="button"
                            className="pj-icon-btn pj-icon-btn-danger flex-shrink-0"
                            title="ลบ IP นี้"
                            onClick={() => removeEnvIp(row.rowId, index)}
                            disabled={ips.length === 1 && !ip}
                          >
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                      ))}
                      <button type="button" className="pj-icon-btn align-self-start" onClick={() => addEnvIp(row.rowId)}>
                        <i className="fa-solid fa-plus" /><span>เพิ่ม IP</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="pj-hint mt-0 mb-1">ยังไม่มี environment — เพิ่มไว้เพื่อใช้เก็บไฟล์ ENV ของแอปนี้</div>
          )}

          <div>
            <button type="button" className="pj-icon-btn pj-icon-btn-accent" onClick={() => setEnvironments((current) => [...current, newEnvRow()])}>
              <i className="fa-solid fa-plus" /><span>เพิ่ม Environment</span>
            </button>
          </div>
        </div>

        <div className="pj-form-section">หมายเหตุ</div>
        <div className="row g-3">
          <div className="col-12">
            <textarea name="note" className="form-control pj-input" rows={2} value={formData.note} onChange={handleChange} placeholder="รายละเอียดเพิ่มเติมของแอปนี้" />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 py-3" style={{ background: "#f7fbff", borderTop: "1px solid #e8f1f9" }}>
        <div className="d-flex flex-column flex-sm-row gap-2 w-100 justify-content-end">
          <button type="button" className="pj-btn pj-btn-ghost" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="pj-btn pj-btn-primary" onClick={() => onSave({ ...formData, environments })}>
            <i className="fa-solid fa-check me-2" />บันทึกข้อมูล
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const VAULT_UNLOCK_SECONDS = 300;

// นับถอยหลังตั๋วปลดล็อกแบบ m:ss อ่านง่ายกว่าตัวเลขวินาทีล้วนเมื่ออายุยาวขึ้น
const formatCountdown = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
const CLIPBOARD_CLEAR_SECONDS = 30;

// เขียนค่าลงคลิปบอร์ดแล้วล้างทิ้งอัตโนมัติ
// ค่าที่ค้างในคลิปบอร์ดคือค่าที่แอปอื่นในเครื่องอ่านได้จนกว่าจะมีการก๊อปอย่างอื่นทับ
const copyThenClear = async (value) => {
  await navigator.clipboard.writeText(value);

  setTimeout(async () => {
    try {
      await navigator.clipboard.writeText("");
    } catch (err) {
      // เขียนคลิปบอร์ดไม่ได้ตอนแท็บไม่โฟกัส ปล่อยผ่าน ไม่ให้หน้าเว็บพัง
    }
  }, CLIPBOARD_CLEAR_SECONDS * 1000);
};

// แผงตั้งค่า/ปลดล็อกด้วยแอป Authenticator ของแต่ละคน
const VaultGate = ({ status, onStatusChange, onUnlocked }) => {
  const [code, setCode] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [password, setPassword] = useState("");

  const startSetup = async () => {
    if (!password) return;
    loading(true);
    const result = await POST("v1/admin/projects/vault/mfa/setup", { password: btoa(password) });
    loading(false);
    setPassword("");
    if (result?.status) setSetupData(result.data);
  };

  const confirmSetup = async () => {
    loading(true);
    const result = await POST("v1/admin/projects/vault/mfa/confirm", { code: code.trim() });
    loading(false);

    if (result?.status) {
      setSetupData(null);
      setCode("");
      setRecoveryCodes(result.data?.recovery_codes || []);
      onStatusChange();
    }
  };

  const unlock = async () => {
    loading(true);
    const result = await POST("v1/admin/projects/vault/unlock", { code: code.trim() });
    loading(false);

    if (result?.status) {
      setCode("");
      onUnlocked(result.data?.vault_token, result.data?.expires_in || VAULT_UNLOCK_SECONDS);
    }
  };

  const disable = async () => {
    if (!(await alert("", "warning", "ปิด Authenticator", "ปิดแล้วจะเปิดไฟล์ ENV ไม่ได้จนกว่าจะตั้งใหม่", "ปิดใช้งาน", "ยกเลิก"))) return;

    loading(true);
    const result = await POST("v1/admin/projects/vault/mfa/disable", { code: code.trim() });
    loading(false);

    if (result?.status) {
      setCode("");
      onStatusChange();
    }
  };

  if (recoveryCodes) {
    return (
      <div className="pj-panel pj-panel-danger">
        <h5 className="pj-panel-title" style={{ color: "#a02a1e" }}>
          <i className="fa-solid fa-life-ring me-2" />รหัสสำรอง — แสดงครั้งเดียวเท่านั้น
        </h5>
        <p className="small mb-3" style={{ color: "#4f6d83" }}>
          เก็บไว้ในที่ปลอดภัยนอกเครื่อง ใช้ตอนทำโทรศัพท์หาย รหัสแต่ละตัวใช้ได้ครั้งเดียว
          ระบบเก็บไว้แค่ค่าแฮช ปิดหน้านี้แล้วดูซ้ำไม่ได้
        </p>
        <div className="row g-2 mb-3">
          {recoveryCodes.map((item) => (
            <div className="col-6 col-md-3" key={item}>
              <div className="pj-recovery-code">{item}</div>
            </div>
          ))}
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="pj-btn pj-btn-ghost" onClick={() => copyText(recoveryCodes.join("\n"))}>
            <i className="fa-regular fa-copy me-2" />คัดลอกทั้งหมด
          </button>
          <button type="button" className="pj-btn pj-btn-primary" onClick={() => setRecoveryCodes(null)}>เก็บเรียบร้อยแล้ว</button>
        </div>
      </div>
    );
  }

  if (setupData) {
    return (
      <div className="pj-panel">
        <h5 className="pj-panel-title mb-3">
          <i className="fa-solid fa-qrcode me-2" style={{ color: "#0c76e5" }} />สแกนด้วยแอป Authenticator
        </h5>
        <div className="d-flex flex-column flex-md-row gap-4 align-items-start">
          <div className="pj-qr">
            <QRCode value={setupData.otpauth_uri} size={168} />
          </div>
          <div className="flex-grow-1">
            <p className="small mb-2" style={{ color: "#4f6d83" }}>
              ใช้ Google Authenticator, Authy, 1Password หรือแอปอะไรก็ได้ที่รองรับ TOTP
              สแกนไม่ได้ให้กรอกรหัสนี้เอง:
            </p>
            <div className="pj-secret mb-3">
              {setupData.secret}
              <CopyButton value={setupData.secret} />
            </div>
            <label className="form-label" style={modalLabelStyle}>กรอกรหัส 6 หลักจากแอปเพื่อยืนยัน</label>
            <div className="d-flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                className="form-control pj-input"
                style={{ ...monoStyle, maxWidth: 160, letterSpacing: "0.3em", textAlign: "center" }}
                value={code}
                maxLength={6}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                onKeyDown={(event) => event.key === "Enter" && confirmSetup()}
                autoComplete="one-time-code"
              />
              <button type="button" className="pj-btn pj-btn-primary" onClick={confirmSetup}>ยืนยัน</button>
              <button type="button" className="pj-btn pj-btn-ghost" onClick={() => { setSetupData(null); setCode(""); }}>ยกเลิก</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!status?.enabled) {
    return (
      <div className="pj-panel pj-panel-danger text-center">
        <i className="fa-solid fa-mobile-screen-button mb-3" style={{ fontSize: 34, color: "#c0392b" }} />
        <h5 className="pj-panel-title justify-content-center mb-2">ต้องตั้งค่าแอป Authenticator ก่อน</h5>
        <p className="small mb-3" style={{ color: "#4f6d83" }}>
          บัญชีนี้ยังไม่ได้ผูกแอป Authenticator — ผูกก่อนถึงจะเปิดดูหรือแก้ไฟล์ ENV ได้
          แต่ละคนมีรหัสของตัวเองแยกกัน ไม่มีรหัสกลางที่ใช้ร่วมกัน
        </p>
        <label className="form-label d-block" style={modalLabelStyle}>กรอกรหัสผ่านของบัญชีนี้เพื่อยืนยันตัวตน</label>
        <div className="d-flex justify-content-center mb-3">
          <input
            type="password"
            className="form-control pj-input"
            style={{ maxWidth: 280 }}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && startSetup()}
            autoComplete="current-password"
            placeholder="รหัสผ่าน"
          />
        </div>
        <button type="button" className="pj-btn pj-btn-primary" onClick={startSetup} disabled={!password}>
          <i className="fa-solid fa-mobile-screen-button me-2" />ตั้งค่า Authenticator
        </button>
      </div>
    );
  }

  return (
    <div className="pj-panel">
      <div className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center">
        <div>
          <h5 className="pj-panel-title mb-1">
            <i className="fa-solid fa-shield-halved me-2" style={{ color: "#0c76e5" }} />ยืนยันด้วย Authenticator
          </h5>
          <p className="small mb-0" style={{ color: "#4f6d83" }}>
            ปลดล็อกได้ครั้งละ {Math.round(VAULT_UNLOCK_SECONDS / 60)} นาที · รหัสสำรองเหลือ {status?.recovery_codes_left ?? 0} ตัว
          </p>
        </div>
        <div className="d-flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            className="form-control pj-input"
            style={{ ...monoStyle, width: 150, letterSpacing: "0.3em", textAlign: "center" }}
            value={code}
            maxLength={11}
            placeholder="000000"
            onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^0-9A-F-]/g, ""))}
            onKeyDown={(event) => event.key === "Enter" && unlock()}
            autoComplete="one-time-code"
          />
          <button type="button" className="pj-btn pj-btn-primary" onClick={unlock}>
            <i className="fa-solid fa-unlock me-2" />ปลดล็อก
          </button>
        </div>
      </div>
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid #eef4f9" }}>
        <button type="button" className="btn btn-link btn-sm p-0" style={{ color: "#a02a1e", textDecoration: "none" }} onClick={disable}>
          ปิดใช้งาน Authenticator (ต้องกรอกรหัสในช่องด้านบนก่อน)
        </button>
      </div>
    </div>
  );
};

// หน้าไฟล์ ENV — เก็บเป็นไฟล์เดียวต่อ 1 environment แก้เป็นข้อความได้ทั้งก้อน
// ก่อนปลดล็อกจะเห็นแค่รายชื่อคีย์กับจำนวน ไม่มีเนื้อไฟล์ส่งมาที่เบราว์เซอร์เลย
const EnvFileModal = ({ show, onHide, environment, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [content, setContent] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [mfaStatus, setMfaStatus] = useState(null);
  const [vaultToken, setVaultToken] = useState("");
  const [unlockUntil, setUnlockUntil] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [reason, setReason] = useState("");

  const envUid = environment?.uid;
  const isUnlocked = !!vaultToken && secondsLeft > 0;
  const vaultHeaders = vaultToken ? { "x-vault-token": vaultToken } : undefined;

  const loadStatus = useCallback(async () => {
    const result = await GET("v1/admin/projects/vault/mfa");
    if (result?.status) setMfaStatus(result.data);
  }, []);

  const loadInfo = useCallback(async () => {
    if (!envUid) return;

    loading(true);
    const result = await GET(`v1/admin/projects/environments/${envUid}/env-file`);
    loading(false);

    if (result?.status) setInfo(result.data);
  }, [envUid]);

  useEffect(() => {
    if (!show) {
      setInfo(null);
      setContent("");
      setLoaded(false);
      setDirty(false);
      setVaultToken("");
      setUnlockUntil(0);
      setReason("");
      return;
    }

    loadStatus();
    loadInfo();
  }, [show, loadStatus, loadInfo]);

  // ตั๋วหมดอายุ = ล้างเนื้อไฟล์ออกจากหน่วยความจำของหน้าเว็บทันที
  useEffect(() => {
    if (!unlockUntil) {
      setSecondsLeft(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((unlockUntil - Date.now()) / 1000));
      setSecondsLeft(remaining);

      if (remaining === 0) {
        setVaultToken("");
        setContent("");
        setLoaded(false);
        setDirty(false);
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [unlockUntil]);

  const openFile = async () => {
    if (reason.trim().length < 5) {
      await alert("", "warning", "แจ้งเตือน", "ต้องระบุเหตุผลที่เปิดไฟล์อย่างน้อย 5 ตัวอักษรก่อน");
      return;
    }

    loading(true);
    const result = await POST(`v1/admin/projects/environments/${envUid}/env-file/reveal`, { reason: reason.trim() }, vaultHeaders);
    loading(false);

    if (result?.status) {
      setContent(result.data?.content || "");
      setLoaded(true);
      setDirty(false);
    }
  };

  const saveFile = async () => {
    if (!(await alert("", "info", "ยืนยัน", "บันทึกทับไฟล์เดิมทั้งก้อน?", "บันทึก", "ยกเลิก"))) return;

    loading(true);
    const result = await PUT(`v1/admin/projects/environments/${envUid}/env-file`, { content }, vaultHeaders);
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", `บันทึกแล้ว ${result.data?.key_count || 0} คีย์`);
      setDirty(false);
      loadInfo();
      onChanged?.();
    }
  };

  const deleteFile = async () => {
    if (!(await alert("", "warning", "ลบไฟล์ ENV", "ลบไฟล์ ENV ของ environment นี้ทั้งก้อน?", "ลบ", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`v1/admin/projects/environments/${envUid}/env-file`, null, vaultHeaders);
    loading(false);

    if (result?.status) {
      setContent("");
      setLoaded(false);
      setDirty(false);
      loadInfo();
      onChanged?.();
    }
  };

  const meta = envMeta(environment?.env_type);
  const file = info?.file;
  const lineCount = content ? content.split(/\r?\n/).length : 0;

  return (
    <Modal show={show} onHide={onHide} centered size="xl" contentClassName="pj-modal border-0 overflow-hidden" dialogClassName="modal-dialog-scrollable">
      <Modal.Header closeButton closeVariant="white" className="border-0 px-4 py-4" style={modalHeaderStyle}>
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
          <span className="pj-modal-icon"><i className="fa-solid fa-key" /></span>
          <div style={{ minWidth: 0 }}>
            <div className="pj-eyebrow text-truncate" style={{ color: "rgba(226, 243, 255, 0.70)", maxWidth: 420 }}>
              {environment?.project_name} · {environment?.app_name} {environment?.name ? `· ${environment.name}` : ""}
            </div>
            <Modal.Title className="fw-bold text-white mb-0 d-flex align-items-center gap-2" style={{ fontSize: "1.3rem", letterSpacing: "-0.01em" }}>
              ไฟล์ ENV
              <span className="pj-env-tag" style={{ background: "rgba(255,255,255,0.16)", color: "#ffffff" }}>{meta.short}</span>
            </Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-4" style={{ background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)" }}>
        <div className="pj-callout pj-callout-danger mb-3">
          <i className="fa-solid fa-shield-halved" />
          <span>ไฟล์ถูกเข้ารหัสทั้งก้อนด้วยกุญแจแยกต่อ environment · เปิดอ่านทีเดียวได้ทั้งไฟล์ของ environment นี้ ทุกครั้งที่เปิดจะถูกบันทึกและแจ้งเตือนออกนอกระบบ</span>
        </div>

        {!isUnlocked ? (
          <div className="mb-3">
            <VaultGate
              status={mfaStatus}
              onStatusChange={loadStatus}
              onUnlocked={(token, expiresIn) => {
                setVaultToken(token);
                setUnlockUntil(Date.now() + expiresIn * 1000);
              }}
            />
          </div>
        ) : (
          <div className="pj-unlock-bar mb-3">
            <span className="fw-bold d-inline-flex align-items-center gap-2">
              <i className="fa-solid fa-lock-open" />ปลดล็อกอยู่
              <span className="pj-unlock-timer">{formatCountdown(secondsLeft)}</span>
            </span>
            <button
              type="button"
              className="pj-icon-btn pj-icon-btn-lock"
              onClick={() => { setVaultToken(""); setUnlockUntil(0); setContent(""); setLoaded(false); setDirty(false); }}
            >
              <i className="fa-solid fa-lock" /><span>ล็อกทันที</span>
            </button>
          </div>
        )}

        <div className="pj-panel mb-3">
          <div className="pj-stat-row">
            <div className="pj-stat">
              <span className="pj-stat-label">จำนวนคีย์</span>
              <span className="pj-stat-value">{file?.key_count ?? 0}</span>
            </div>
            <div className="pj-stat">
              <span className="pj-stat-label">ขนาดไฟล์</span>
              <span className="pj-stat-value">{file?.content_length ?? 0} <small>ตัวอักษร</small></span>
            </div>
            <div className="pj-stat">
              <span className="pj-stat-label">แก้ล่าสุด</span>
              <span className="pj-stat-value">{file?.updated_at ? formatDate(file.updated_at) : "-"}</span>
            </div>
          </div>
          {file?.key_names?.length ? (
            <div className="mt-3 pt-3 d-flex flex-wrap gap-2" style={{ borderTop: "1px dashed #e6eff7" }}>
              {file.key_names.map((key) => (
                <span key={key} className="pj-key-chip">{key}</span>
              ))}
            </div>
          ) : (
            <div className="pj-hint mt-2">ยังไม่มีไฟล์ ENV — ปลดล็อกแล้วพิมพ์หรือวางเนื้อไฟล์ได้เลย</div>
          )}
        </div>

        {isUnlocked && !loaded ? (
          <div className="pj-panel mb-3">
            <label className="form-label" style={modalLabelStyle}>เหตุผลที่ต้องเปิดไฟล์ (บันทึกลงประวัติ)</label>
            <div className="d-flex flex-column flex-md-row gap-2">
              <input
                type="text"
                className="form-control pj-input"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="เช่น ย้ายเซิร์ฟเวอร์ CMMS production"
                maxLength={300}
                onKeyDown={(event) => event.key === "Enter" && openFile()}
              />
              <button type="button" className="pj-btn pj-btn-primary flex-shrink-0" onClick={openFile}>
                <i className="fa-solid fa-unlock me-2" />เปิดไฟล์
              </button>
            </div>
            <div className="pj-hint">ไฟล์ยังไม่ถูกส่งมาที่เบราว์เซอร์จนกว่าจะกดเปิด — กดสร้างไฟล์ใหม่ได้เลยถ้ายังไม่มี</div>
          </div>
        ) : null}

        {isUnlocked && loaded ? (
          <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <label className="form-label mb-0" style={modalLabelStyle}>เนื้อไฟล์ .env</label>
              <div className="d-flex gap-2 align-items-center">
                <span className="small" style={{ color: "#8aa4b8", fontVariantNumeric: "tabular-nums" }}>{lineCount} บรรทัด · {content.length} ตัวอักษร</span>
                <button type="button" className="pj-icon-btn" onClick={() => copyThenClear(content)}>
                  <i className="fa-regular fa-copy" /><span>คัดลอกทั้งไฟล์</span>
                </button>
              </div>
            </div>
            <textarea
              className="form-control pj-input pj-env-editor"
              style={{ ...monoStyle, minHeight: 360, lineHeight: 1.75, whiteSpace: "pre", overflowWrap: "normal", overflowX: "auto" }}
              value={content}
              spellCheck={false}
              onChange={(event) => { setContent(event.target.value); setDirty(true); }}
              placeholder={"NODE_ENV=production\nDB_HOST=127.0.0.1\nDB_PASSWORD=..."}
            />
            <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
              <button type="button" className="pj-btn pj-btn-danger" onClick={deleteFile}>
                <i className="fa-solid fa-trash me-2" />ลบไฟล์นี้
              </button>
              <div className="d-flex gap-2 align-items-center">
                {dirty ? <span className="pj-dirty"><span className="pj-dirty-dot" />ยังไม่ได้บันทึก</span> : null}
                <button type="button" className="pj-btn pj-btn-primary" onClick={saveFile}>
                  <i className="fa-solid fa-check me-2" />บันทึกไฟล์
                </button>
              </div>
            </div>
            <div className="pj-hint">
              คัดลอกทั้งไฟล์แล้วคลิปบอร์ดจะถูกล้างอัตโนมัติใน {CLIPBOARD_CLEAR_SECONDS} วินาที · ตั๋วหมดอายุเมื่อไหร่ เนื้อไฟล์ในหน้านี้จะถูกล้างทิ้งทันที
            </div>
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 py-3" style={{ background: "#f7fbff", borderTop: "1px solid #e8f1f9" }}>
        <button type="button" className="pj-btn pj-btn-ghost" onClick={onHide}>ปิด</button>
      </Modal.Footer>
    </Modal>
  );
};

// ป้าย environment แบบกดได้ — ย่อข้อมูลทั้ง environment เหลือชิปเดียว
// รายละเอียดค่อยกางเมื่อกด เพราะ 51 environment ถ้ากางหมดพร้อมกันหน้าจะยาวจนหาอะไรไม่เจอ
const EnvChip = ({ environment, active, onClick }) => {
  const meta = envMeta(environment.env_type);
  const warning = [environment.ssl_expire_date, environment.domain_expire_date]
    .map(expireState)
    .some((item) => item && item.days <= 30);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`env-chip ${active ? "is-active" : ""}`}
      title={environment.name || meta.label}
      style={{
        background: active ? meta.color : meta.background,
        color: active ? "#ffffff" : meta.color,
        borderColor: active ? meta.color : `${meta.color}33`,
        boxShadow: active ? `0 8px 18px -8px ${meta.color}` : "none",
      }}
    >
      <span className="env-chip-dot" style={{ background: active ? "rgba(255,255,255,0.85)" : meta.color }} />
      <span className="fw-bold" style={{ letterSpacing: "0.04em" }}>{meta.short}</span>
      <span className="env-chip-count" style={{ background: active ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.72)", color: active ? "#ffffff" : meta.color }}>
        <i className="fa-solid fa-key" style={{ fontSize: "0.6rem", opacity: 0.75 }} /> {environment.env_key_count || 0}
      </span>
      {warning ? <i className="fa-solid fa-triangle-exclamation env-chip-warn" style={{ fontSize: "0.7rem" }} /> : null}
    </button>
  );
};

// แถวปลายทาง 1 บรรทัด: ไอคอน + ป้ายกำกับ + URL ที่กดได้ + ปุ่มคัดลอก
// ตัด protocol ออกจากที่แสดงเพื่อให้อ่านโดเมนได้ทันที ส่วน URL เต็มเก็บไว้ใน title กับตอนคัดลอก
const EndpointRow = ({ label, icon, url }) => {
  const href = normalizeUrl(url);

  if (!href) {
    return (
      <div className="env-endpoint is-empty">
        <span className="env-endpoint-icon"><i className={`fa-solid ${icon}`} /></span>
        <span className="env-endpoint-label">{label}</span>
        <span className="env-endpoint-dash">-</span>
      </div>
    );
  }

  return (
    <div className="env-endpoint">
      <span className="env-endpoint-icon"><i className={`fa-solid ${icon}`} /></span>
      <span className="env-endpoint-label">{label}</span>
      <a className="env-endpoint-url" href={href} target="_blank" rel="noopener noreferrer" title={href}>
        {href.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "")}
      </a>
      <span className="env-endpoint-actions">
        <button type="button" title="คัดลอกลิงก์" onClick={() => copyText(href)}>
          <i className="fa-regular fa-copy" />
        </button>
        <a href={href} target="_blank" rel="noopener noreferrer" title="เปิดในแท็บใหม่">
          <i className="fa-solid fa-arrow-up-right-from-square" />
        </a>
      </span>
    </div>
  );
};

// modal แสดง IP ของ server ใน environment เดียว — แยกออกจากรายละเอียดหลัก
// เพราะบาง env มีหลายเครื่อง ถ้ากางไว้ในการ์ดจะดันข้อมูลสถานะลงไปจนหาไม่เจอ
const ServerIpModal = ({ show, onHide, environment }) => {
  const meta = envMeta(environment?.env_type);
  const ips = (environment?.server_ips || []).filter(Boolean);

  // โทนสีทั้ง modal อิงสีของ environment เดียวกับชิป/การ์ด จะได้รู้ทันทีว่ากำลังดู IP ของ env ไหน
  const headerStyle = {
    background: `radial-gradient(circle at 88% -30%, rgba(255, 255, 255, 0.28), transparent 46%), linear-gradient(120deg, color-mix(in srgb, ${meta.color} 72%, #0b1f33) 0%, ${meta.color} 58%, color-mix(in srgb, ${meta.color} 78%, #ffffff) 100%)`,
  };
  const bodyStyle = {
    "--env-color": meta.color,
    background: `linear-gradient(180deg, #ffffff 0%, color-mix(in srgb, ${meta.color} 7%, #ffffff) 100%)`,
  };
  const footerStyle = {
    "--env-color": meta.color,
    background: `color-mix(in srgb, ${meta.color} 5%, #ffffff)`,
    borderTop: `1px solid color-mix(in srgb, ${meta.color} 14%, #e8f1f9)`,
  };

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="pj-modal border-0 overflow-hidden">
      <Modal.Header closeButton closeVariant="white" className="border-0 px-4 py-4" style={headerStyle}>
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
          <span className="pj-modal-icon"><i className="fa-solid fa-server" /></span>
          <div style={{ minWidth: 0 }}>
            <div className="pj-eyebrow text-truncate" style={{ color: "rgba(226, 243, 255, 0.70)", maxWidth: 420 }}>
              {environment?.project_name} · {environment?.app_name} {environment?.name ? `· ${environment.name}` : ""}
            </div>
            <Modal.Title className="fw-bold text-white mb-0 d-flex align-items-center gap-2" style={{ fontSize: "1.3rem", letterSpacing: "-0.01em" }}>
              IP Server
              <span className="pj-env-tag" style={{ background: "rgba(255,255,255,0.16)", color: "#ffffff" }}>{meta.short}</span>
            </Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-4" style={bodyStyle}>
        {ips.length ? (
          <div className="env-ip-list">
            {ips.map((ip, index) => (
              <div className="env-ip-item" key={`${ip}-${index}`}>
                <span className="env-ip-index">{index + 1}</span>
                <span className="env-ip-value">{ip}</span>
                <button type="button" className="pj-icon-btn" title="คัดลอก IP" onClick={() => copyText(ip)}>
                  <i className="fa-regular fa-copy" /><span>คัดลอก</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="pj-empty py-4">
            <i className="fa-solid fa-server" />
            <div>ยังไม่ได้ระบุ IP ของ server ใน environment นี้</div>
            <div className="pj-hint mt-1">กดแก้ไขแอปเพื่อเพิ่ม IP ในส่วน Environment</div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 py-3" style={footerStyle}>
        <div className="d-flex gap-2 w-100 justify-content-end">
          {ips.length > 1 ? (
            <button type="button" className="pj-btn pj-btn-ghost" onClick={() => copyText(ips.join("\n"))}>
              <i className="fa-regular fa-copy me-2" />คัดลอกทั้งหมด
            </button>
          ) : null}
          <button type="button" className="pj-btn pj-btn-env" onClick={onHide}>ปิด</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// รายละเอียด environment ที่ถูกเลือก แสดงทีละตัว
// จัดเป็นการ์ดย่อย 3 กลุ่ม (ปลายทาง / เซิร์ฟเวอร์ / สถานะ) เพื่อให้กวาดสายตาหาค่าที่ต้องการได้เร็ว
const EnvDetail = ({ environment, projectName, appName, onOpenEnvFile }) => {
  const [showServerIps, setShowServerIps] = useState(false);
  const meta = envMeta(environment.env_type);
  const ssl = expireState(environment.ssl_expire_date);
  const domain = expireState(environment.domain_expire_date);

  const expiryValue = (state, rawDate) => {
    if (!state) return null;
    const urgent = state.days <= 30;
    return (
      <span className="d-inline-flex align-items-center gap-2">
        <span style={{ color: urgent ? state.tone : "#33566f", fontWeight: urgent ? 700 : 500 }}>{state.text}</span>
        {urgent ? <span className="pj-expiry-pill" style={{ background: `${state.tone}18`, color: state.tone }}>{formatDate(rawDate)}</span> : null}
      </span>
    );
  };

  // ปลายทางย้ายไปรวมที่แผง "ปลายทาง" ระดับโปรเจคด้านบนแล้ว ตรงนี้เหลือเฉพาะสถานะ
  // ข้อมูลเครื่อง (host/path/db) ไม่แสดงตรงนี้ กดปุ่มแก้ไขเพื่อดูหรือแก้ค่าได้
  const statusRows = [
    ["เวอร์ชัน", environment.version ? <span style={{ ...monoStyle, fontWeight: 700, color: "#11324c" }}>{environment.version}</span> : null],
    ["SSL", expiryValue(ssl, environment.ssl_expire_date)],
    ["โดเมน", expiryValue(domain, environment.domain_expire_date)],
  ].filter(([, value]) => value);

  const serverIps = (environment.server_ips || []).filter(Boolean);

  return (
    <div className="env-detail mt-3" style={{ "--env-color": meta.color }}>
      <div className="env-detail-bar" style={{ background: `linear-gradient(180deg, ${meta.color} 0%, ${meta.color}99 100%)` }} />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="pj-env-tag" style={{ background: meta.background, color: meta.color }}>{meta.short}</span>
          <span className="fw-bold" style={{ color: "#11324c", fontSize: "0.98rem" }}>{meta.label}</span>
          {environment.name ? <span className="small" style={{ color: "#7b93a8" }}>· {environment.name}</span> : null}
        </div>

        <div className="d-flex gap-2 flex-shrink-0">
          <button className="pj-icon-btn pj-icon-btn-server" title="ดู IP ของ server" onClick={() => setShowServerIps(true)}>
            <i className="fa-solid fa-server" />
            <span>IP Server</span>
            <span className="pj-icon-btn-count">{serverIps.length}</span>
          </button>
          {isSuperUser ? (
            <button
              className="pj-icon-btn pj-icon-btn-vault"
              title="เปิดไฟล์ ENV"
              onClick={() => onOpenEnvFile({ ...environment, project_name: projectName, app_name: appName })}
            >
              <i className="fa-solid fa-key" />
              <span>ไฟล์ ENV</span>
              <span className="pj-icon-btn-count">{environment.env_key_count || 0}</span>
            </button>
          ) : null}
        </div>
      </div>

      <ServerIpModal
        show={showServerIps}
        onHide={() => setShowServerIps(false)}
        environment={{ ...environment, project_name: projectName, app_name: appName }}
      />

      {statusRows.length ? (
        <div className="env-group">
          <div className="env-group-title">
            <i className="fa-solid fa-shield-halved" />
            สถานะ
          </div>
          <div className="row g-0">
            {statusRows.map(([label, value]) => (
              <div className="col-12 col-md-4" key={label}>
                <div className="env-kv">
                  <span className="env-kv-label">{label}</span>
                  <span className="env-kv-value">{value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// 4 ช่องปลายทางของ 1 environment เรียงตามลำดับที่คนมองหาบ่อยสุดลงไปหาน้อยสุด
const ENDPOINT_FIELDS = [
  { key: "web_url", label: "เว็บ", icon: "fa-globe", placeholder: "https://app.example.com" },
  { key: "api_base_url", label: "API", icon: "fa-plug", placeholder: "https://api.example.com" },
  { key: "health_check_url", label: "Health", icon: "fa-heart-pulse", placeholder: "https://api.example.com/health" },
  { key: "storage_url", label: "Storage", icon: "fa-box-archive", placeholder: "https://s3.example.com/bucket" },
];

const endpointDraftFrom = (envType, endpoint) => ({
  uid: endpoint?.uid || "",
  env_type: envType,
  web_url: endpoint?.web_url || "",
  api_base_url: endpoint?.api_base_url || "",
  health_check_url: endpoint?.health_check_url || "",
  storage_url: endpoint?.storage_url || "",
});

// แผงปลายทางระดับโปรเจค — ข้อมูลชุดเดียวต่อ 1 environment ไม่แยกตามแอป
// ที่แยกออกมาไว้บนสุดเพราะ "เว็บ prod อยู่ไหน" คือคำถามที่ถูกถามบ่อยที่สุด
// ไม่ควรต้องกดเข้าไปทีละแอปทีละชิปถึงจะเจอ
const ProjectEndpoints = ({ project, onChanged }) => {
  const [activeType, setActiveType] = useState("");
  const [draft, setDraft] = useState(null); // null = โหมดอ่าน, มีค่า = กำลังแก้ไข/เพิ่ม

  const tabs = useMemo(() => {
    const list = project?.endpoints || [];
    // เรียงตามลำดับใน ENV_TYPES เพื่อให้ PROD อยู่ซ้ายสุดเสมอ ไม่ขึ้นกับลำดับที่สร้าง
    return ENV_TYPES.map((item) => ({ ...item, endpoint: list.find((row) => row.env_type === item.value) })).filter((item) => item.endpoint);
  }, [project]);

  // เปลี่ยนโปรเจคแล้วต้องทิ้งฟอร์มที่ค้างอยู่ ไม่งั้นจะเผลอบันทึกทับโปรเจคอื่น
  useEffect(() => {
    setDraft(null);
  }, [project?.uid]);

  useEffect(() => {
    if (!tabs.length) {
      if (activeType) setActiveType("");
      return;
    }
    if (!tabs.some((item) => item.value === activeType)) setActiveType(tabs[0].value);
  }, [tabs, activeType]);

  const active = tabs.find((item) => item.value === activeType) || tabs[0] || null;
  const remaining = ENV_TYPES.filter((item) => !tabs.some((tab) => tab.value === item.value));
  const accent = (draft ? envMeta(draft.env_type) : active)?.color || ENV_TYPES[3].color;

  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const handleSave = async () => {
    const body = Object.fromEntries(ENDPOINT_FIELDS.map((field) => [field.key, draft[field.key]?.trim() ?? ""]));

    loading(true);
    // keepEmpty เพื่อให้ช่องที่ล้างค่าถูกส่งขึ้นไปจริง ไม่ถูก removeEmptyString ตัดทิ้ง
    const result = draft.uid
      ? await PUT(`v1/admin/projects/endpoints/${draft.uid}`, body, undefined, { keepEmpty: true })
      : await POST("v1/admin/projects/endpoints", { ...body, project_uid: project.uid, env_type: draft.env_type });
    loading(false);

    if (result?.status) {
      setActiveType(draft.env_type);
      setDraft(null);
      onChanged();
    }
  };

  const handleDelete = async () => {
    if (!(await alert("", "warning", "ลบปลายทาง", `ต้องการลบปลายทางของ ${active.label} ?`, "ลบ", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`v1/admin/projects/endpoints/${active.endpoint.uid}`);
    loading(false);

    if (result?.status) {
      setActiveType("");
      onChanged();
    }
  };

  if (!tabs.length && !draft) {
    if (!can("projects.create")) return null;

    return (
      <div className="endpoint-board endpoint-board-empty">
        <div className="endpoint-board-title mb-2">
          <i className="fa-solid fa-globe" />
          ปลายทาง
        </div>
        <p className="small mb-3" style={{ color: "#9db4c7" }}>ยังไม่ได้ระบุปลายทางของโปรเจคนี้</p>
        <button type="button" className="pj-btn pj-btn-primary" onClick={() => setDraft(endpointDraftFrom(ENV_TYPES[0].value))}>
          <i className="fa-solid fa-plus me-2" />เพิ่มปลายทาง
        </button>
      </div>
    );
  }

  return (
    <div className="endpoint-board" style={{ "--env-color": accent }}>
      <div className="endpoint-board-head">
        <div className="endpoint-board-title">
          <i className="fa-solid fa-globe" />
          ปลายทาง
        </div>

        <div className="endpoint-toolbar">
          <div className="endpoint-tabs">
            {tabs.map((item) => {
              const isActive = !draft && item.value === active?.value;

              return (
                <button
                  type="button"
                  key={item.value}
                  className={`endpoint-tab ${isActive ? "is-active" : ""}`}
                  onClick={() => {
                    setDraft(null);
                    setActiveType(item.value);
                  }}
                  style={{
                    background: isActive ? item.color : "transparent",
                    color: isActive ? "#ffffff" : item.color,
                    borderColor: isActive ? item.color : `${item.color}33`,
                    opacity: draft ? 0.5 : 1,
                  }}
                  title={item.label}
                  disabled={!!draft}
                >
                  {item.short}
                </button>
              );
            })}

            {can("projects.create") && remaining.length && !draft ? (
              <button
                type="button"
                className="endpoint-tab endpoint-tab-add"
                title="เพิ่ม environment"
                onClick={() => setDraft(endpointDraftFrom(remaining[0].value))}
              >
                <i className="fa-solid fa-plus" />
              </button>
            ) : null}
          </div>

          <div className="endpoint-actions">
            {draft ? (
              <>
                <button type="button" className="pj-icon-btn" onClick={() => setDraft(null)}>ยกเลิก</button>
                <button type="button" className="pj-icon-btn pj-icon-btn-accent" onClick={handleSave}>
                  <i className="fa-solid fa-check" /><span>บันทึก</span>
                </button>
              </>
            ) : (
              <>
                {can("projects.update") ? (
                  <button
                    type="button"
                    className="pj-icon-btn"
                    title="แก้ไขปลายทาง"
                    onClick={() => setDraft(endpointDraftFrom(active.value, active.endpoint))}
                  >
                    <i className="fa-solid fa-pen" />
                  </button>
                ) : null}
                {can("projects.delete") ? (
                  <button type="button" className="pj-icon-btn pj-icon-btn-danger" title="ลบปลายทาง" onClick={handleDelete}>
                    <i className="fa-solid fa-trash" />
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {draft ? (
        <div className="endpoint-form">
          {!draft.uid ? (
            <div className="endpoint-form-row">
              <span className="endpoint-form-label">Environment</span>
              <div className="flex-grow-1">
                <Select
                  className="form-control pj-input"
                  value={draft.env_type}
                  options={remaining.map(({ value, label }) => ({ value, label }))}
                  default={false}
                  placeholder="เลือก environment"
                  onChange={(value) => setField("env_type", value || remaining[0]?.value || "")}
                />
              </div>
            </div>
          ) : null}

          {ENDPOINT_FIELDS.map((field) => (
            <div className="endpoint-form-row" key={field.key}>
              <span className="endpoint-form-label">
                <i className={`fa-solid ${field.icon}`} />
                {field.label}
              </span>
              <input
                type="text"
                className="form-control pj-input flex-grow-1"
                value={draft[field.key]}
                placeholder={field.placeholder}
                onChange={(event) => setField(field.key, event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSave()}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="env-endpoints">
          {ENDPOINT_FIELDS.map((field) => (
            <EndpointRow key={field.key} label={field.label} icon={field.icon} url={active.endpoint[field.key]} />
          ))}
        </div>
      )}
    </div>
  );
};

// แถวโปรเจคในรายการซ้าย — ย่อเหลือสองบรรทัดเพื่อกวาดสายตาหา 18 โปรเจคได้เร็ว
// ลากเรียงได้ด้วย HTML5 drag & drop (drag เฉพาะที่ handle ซ้ายมือ เพื่อไม่ชนกับการคลิกเลือกโปรเจค)
const ProjectListItem = ({ project, active, onClick, draggable, dragState, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const status = statusMeta(project.status);
  const environments = (project.apps || []).flatMap((app) => app.environments || []);
  const keyCount = environments.reduce((total, env) => total + (env.env_key_count || 0), 0);
  const envTypes = [...new Set(environments.map((env) => env.env_type))];
  // เรียงตามลำดับความสำคัญที่ประกาศไว้ใน ENV_TYPES จุดสีจะได้อยู่ตำแหน่งเดิมทุกแถว
  const orderedEnvTypes = ENV_TYPES.filter((item) => envTypes.includes(item.value));

  return (
    <div
      className={`project-row-wrap ${dragState ? `is-${dragState}` : ""}`}
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {draggable ? (
        <span className="project-row-grip" title="ลากเพื่อจัดลำดับ">
          <i className="fa-solid fa-grip-vertical" />
        </span>
      ) : null}
    <button type="button" className={`project-row ${active ? "active" : ""}`} onClick={onClick}>
      <span className="project-row-rail" style={{ background: status.color }} />

      <div className="d-flex align-items-center gap-2">
        {project.project_code ? <span className="project-code">{project.project_code}</span> : null}
        <span className="project-row-name text-truncate">{project.name}</span>
        <span className="project-row-status ms-auto flex-shrink-0" style={{ background: status.color }} title={status.label} />
      </div>

      <div className="project-row-meta">
        <span><b>{project.apps?.length || 0}</b> แอป</span>
        <span className="project-row-sep" />
        <span><b>{environments.length}</b> env</span>
        {keyCount ? (
          <>
            <span className="project-row-sep" />
            <span><i className="fa-solid fa-key" style={{ fontSize: "0.6rem", opacity: 0.7 }} /> <b>{keyCount}</b></span>
          </>
        ) : null}
        <span className="ms-auto d-flex gap-1 align-items-center">
          {orderedEnvTypes.map((item) => (
            <span key={item.value} className="project-row-envdot" style={{ background: item.color }} title={item.label} />
          ))}
        </span>
      </div>
    </button>
    </div>
  );
};

export default function Projects() {
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [statusInput, setStatusInput] = useState("");
  const [envFilter, setEnvFilter] = useState("");
  const [filters, setFilters] = useState({ data_search: "", status: "" });

  const [projectModal, setProjectModal] = useState({ show: false, editData: null });
  const [appModal, setAppModal] = useState({ show: false, editData: null, projectUid: "", projectName: "" });
  const [envFileModal, setEnvFileModal] = useState({ show: false, environment: null });

  // เลือกดูทีละโปรเจค แทนการกางทุกอันพร้อมกัน (18 โปรเจค 51 แอปกางหมดแล้วหาอะไรไม่เจอ)
  const [selectedUid, setSelectedUid] = useState("");
  const [selectedEnvKey, setSelectedEnvKey] = useState("");

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/projects", {
      page: 0,
      ...(filters.data_search ? { data_search: filters.data_search } : {}),
      ...(filters.status ? { status: filters.status } : {}),
    });
    if (result?.status) setData(result.data || []);
    loading(false);
  }, [filters]);

  useEffect(() => {
    getData();
  }, [getData]);

  // กรอง environment ฝั่งหน้าเว็บ เพราะข้อมูลมาเป็นต้นไม้ทั้งก้อนอยู่แล้ว ไม่ต้องยิง API ซ้ำ
  const visibleProjects = useMemo(() => {
    if (!envFilter) return data;

    return data
      .map((project) => ({
        ...project,
        apps: (project.apps || [])
          .map((app) => ({ ...app, environments: (app.environments || []).filter((env) => env.env_type === envFilter) }))
          .filter((app) => app.environments.length),
      }))
      .filter((project) => project.apps.length);
  }, [data, envFilter]);

  const summary = useMemo(() => {
    const environments = data.flatMap((project) => (project.apps || []).flatMap((app) => app.environments || []));

    return [
      { label: "โปรเจคทั้งหมด", value: data.length, color: "#0c76e5", icon: "fa-diagram-project" },
      { label: "แอปทั้งหมด", value: data.reduce((total, project) => total + (project.apps?.length || 0), 0), color: "#0b4f84", icon: "fa-layer-group" },
      { label: "Production", value: environments.filter((env) => env.env_type === "production").length, color: "#c0392b", icon: "fa-tower-broadcast", filter: "production" },
      { label: "Sandbox / Develop", value: environments.filter((env) => ["sandbox", "develop", "staging", "local"].includes(env.env_type)).length, color: "#b97007", icon: "fa-flask", filter: "develop" },
    ];
  }, [data]);

  const selectedProject = useMemo(
    () => visibleProjects.find((project) => project.uid === selectedUid) || null,
    [visibleProjects, selectedUid]
  );

  // ถ้าตัวที่เลือกหลุดจากผลกรอง (หรือยังไม่ได้เลือก) ให้เด้งไปตัวแรกของรายการ
  useEffect(() => {
    if (!visibleProjects.length) {
      if (selectedUid) setSelectedUid("");
      return;
    }
    if (!visibleProjects.some((project) => project.uid === selectedUid)) {
      setSelectedUid(visibleProjects[0].uid);
      setSelectedEnvKey("");
    }
  }, [visibleProjects, selectedUid]);

  const applyFilters = () => setFilters({ data_search: searchInput.trim(), status: statusInput });

  // ลากเรียงได้เฉพาะตอนที่รายการโชว์ครบทุกโปรเจค ถ้ากรองอยู่ลำดับที่เห็นไม่ใช่ลำดับจริง บันทึกไปจะมั่ว
  const canReorder = can("projects.update") && !envFilter && !filters.data_search && !filters.status;
  const [drag, setDrag] = useState({ uid: "", overUid: "", position: "" });

  const handleDragStart = (event, uid) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", uid);
    setDrag({ uid, overUid: "", position: "" });
  };

  const handleDragOver = (event, uid) => {
    if (!drag.uid || drag.uid === uid) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    const rect = event.currentTarget.getBoundingClientRect();
    const position = event.clientY - rect.top < rect.height / 2 ? "before" : "after";
    if (drag.overUid !== uid || drag.position !== position) setDrag((current) => ({ ...current, overUid: uid, position }));
  };

  const handleDragEnd = () => setDrag({ uid: "", overUid: "", position: "" });

  const handleDrop = async (event, targetUid) => {
    event.preventDefault();
    const { uid: sourceUid, position } = drag;
    handleDragEnd();
    if (!sourceUid || sourceUid === targetUid) return;

    const list = data.filter((project) => project.uid !== sourceUid);
    const moving = data.find((project) => project.uid === sourceUid);
    const targetIndex = list.findIndex((project) => project.uid === targetUid);
    if (!moving || targetIndex < 0) return;

    list.splice(position === "after" ? targetIndex + 1 : targetIndex, 0, moving);
    if (list.every((project, index) => project.uid === data[index]?.uid)) return;

    // อัปเดตหน้าจอทันทีให้รู้สึกลื่น แล้วค่อยบันทึก ถ้าบันทึกไม่ผ่านจะโหลดลำดับจริงกลับมาทับ
    setData(list);
    const result = await PUT("v1/admin/projects/reorder", { uids: list.map((project) => project.uid) });
    if (!result?.status) getData();
  };

  // selectedProject มาจากชุดที่ถูกกรองด้วย envFilter แล้ว ถ้าเอา app ตัวนั้นไปเปิดฟอร์ม
  // environment ที่ไม่ตรงตัวกรองจะหายจากรายการ แล้วโดน sync ลบทิ้งตอนบันทึก
  const rawApp = (appUid) => data.flatMap((project) => project.apps || []).find((app) => app.uid === appUid);

  const handleSaveProject = async (formData) => {
    if (!formData.name?.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อโปรเจค");
      return;
    }
    if (!(await alert("", "info", "ยืนยัน", "ต้องการบันทึกข้อมูลนี้?", "บันทึก", "ยกเลิก"))) return;

    const body = {
      project_code: formData.project_code?.trim() || undefined,
      name: formData.name.trim(),
      client_name: formData.client_name?.trim() || undefined,
      project_type: formData.project_type,
      status: formData.status,
      owner: formData.owner?.trim() || undefined,
      start_date: formData.start_date || undefined,
      go_live_date: formData.go_live_date || undefined,
      doc_url: formData.doc_url?.trim() || undefined,
      board_url: formData.board_url?.trim() || undefined,
      description: formData.description?.trim() || undefined,
      note: formData.note?.trim() || undefined,
    };

    loading(true);
    const result = formData.uid ? await PUT(`v1/admin/projects/${formData.uid}`, body) : await POST("v1/admin/projects", body);
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "บันทึกข้อมูลเรียบร้อย");
      setProjectModal({ show: false, editData: null });
      getData();
    }
  };

  const handleDeleteProject = async (project) => {
    if (!(await alert("", "warning", "ลบโปรเจค", `ต้องการลบ ${project.name} ?`, "ลบ", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`v1/admin/projects/${project.uid}`);
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "ลบเรียบร้อย");
      getData();
    }
  };

  const handleSaveApp = async (formData) => {
    if (!formData.name?.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อแอป");
      return;
    }

    const body = {
      name: formData.name.trim(),
      app_type: formData.app_type,
      tech_stack: formData.tech_stack?.trim() || undefined,
      git_url: formData.git_url?.trim() || undefined,
      git_provider: formData.git_provider?.trim() || undefined,
      default_branch: formData.default_branch?.trim() || undefined,
      package_id: formData.package_id?.trim() || undefined,
      store_url: formData.store_url?.trim() || undefined,
      sort_order: Number(formData.sort_order) || 0,
      note: formData.note?.trim() || undefined,
      // ส่งทั้งชุดเสมอ backend จะ sync ให้: มี uid = แก้, ไม่มี uid = สร้าง, ที่หายไป = ลบ
      environments: (formData.environments || []).map((row, index) => ({
        ...(row.uid ? { uid: row.uid } : {}),
        env_type: row.env_type,
        ...(row.name?.trim() ? { name: row.name.trim() } : {}),
        // IP ส่งเป็น array เสมอ (ว่างได้) เพื่อให้ลบออกหมดแล้วบันทึกได้จริง
        server_ips: [...new Set((row.server_ips || []).map((ip) => String(ip || "").trim()).filter(Boolean))],
        sort_order: index,
      })),
    };

    loading(true);
    // keepEmpty เพื่อไม่ให้ environments: [] (ลบออกหมด) ถูก removeEmptyString ตัดทิ้ง
    const result = formData.uid
      ? await PUT(`v1/admin/projects/apps/${formData.uid}`, body, undefined, { keepEmpty: true })
      : await POST("v1/admin/projects/apps", { ...body, project_uid: formData.project_uid });
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "บันทึกข้อมูลเรียบร้อย");
      setAppModal({ show: false, editData: null, projectUid: "", projectName: "" });
      getData();
    }
  };

  const handleDeleteApp = async (app) => {
    if (!(await alert("", "warning", "ลบแอป", `ต้องการลบ ${app.name} ? environment และค่า ENV ที่ผูกอยู่จะถูกลบตามไปด้วย`, "ลบ", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`v1/admin/projects/apps/${app.uid}`);
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "ลบเรียบร้อย");
      getData();
    }
  };

  return (
    <AppShell
      title="ทะเบียนโปรเจค"
      description="เลือกโปรเจคจากรายการซ้าย เพื่อดูแอปและ environment — กดชิป environment เพื่อดูรายละเอียด"
    >
      <div className="row g-2 g-md-3 mb-3">
        {summary.map((card) => {
          const isActive = card.filter && envFilter === card.filter;

          return (
            <div className="col-6 col-xl-3" key={card.label}>
              <button
                type="button"
                className={`summary-card w-100 text-start h-100 ${card.filter ? "is-clickable" : ""} ${isActive ? "is-active" : ""}`}
                style={{ "--card-color": card.color }}
                onClick={() => card.filter && setEnvFilter(envFilter === card.filter ? "" : card.filter)}
                title={card.filter ? "กดเพื่อกรองเฉพาะ environment นี้" : ""}
              >
                <span className="summary-card-glow" />
                <div className="d-flex align-items-center gap-3 position-relative">
                  <div className="summary-card-icon">
                    <i className={`fa-solid ${card.icon}`} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p className="summary-card-label">{card.label}</p>
                    <h3 className="summary-card-value">{card.value}</h3>
                  </div>
                  {card.filter ? (
                    <span className="summary-card-flag ms-auto">
                      {isActive ? <><i className="fa-solid fa-filter-circle-xmark me-1" />กำลังกรอง</> : <i className="fa-solid fa-filter" />}
                    </span>
                  ) : null}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <div className="pj-toolbar mb-3">
        <div className="pj-toolbar-fields">
          <div className="pj-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="text"
              className="form-control pj-search-input"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && applyFilters()}
              placeholder="ค้นหาชื่อโปรเจค รหัส ลูกค้า..."
            />
          </div>
          <div className="pj-field">
            <Select className="form-control pj-input" value={statusInput} options={PROJECT_STATUS.map(({ value, label }) => ({ value, label }))} placeholder="ทุกสถานะ" onChange={(value) => setStatusInput(value)} />
          </div>
          <div className="pj-field">
            <Select className="form-control pj-input" value={envFilter} options={ENV_TYPES.map(({ value, label }) => ({ value, label }))} placeholder="ทุก Environment" onChange={(value) => setEnvFilter(value)} />
          </div>
          <button type="button" className="pj-btn pj-btn-primary" onClick={applyFilters}>
            <i className="fa-solid fa-magnifying-glass me-2" />ค้นหา
          </button>
          <button
            type="button"
            className="pj-btn pj-btn-ghost"
            onClick={() => {
              setSearchInput("");
              setStatusInput("");
              setEnvFilter("");
              setFilters({ data_search: "", status: "" });
            }}
          >
            ล้างค่า
          </button>
        </div>

        {can("projects.create") ? (
          <button onClick={() => setProjectModal({ show: true, editData: null })} className="pj-btn pj-btn-primary flex-shrink-0">
            <i className="fa-solid fa-plus me-2" />เพิ่มโปรเจค
          </button>
        ) : null}
      </div>

      <div className="row g-3">
        {/* รายการโปรเจค */}
        <div className="col-12 col-lg-4 col-xxl-3">
          <div className="project-list-pane" style={sectionCardStyle}>
            <div className="project-list-head">
              <span className="pj-eyebrow" style={{ color: "#5b7c96" }}>โปรเจค</span>
              <span className="project-list-count">{visibleProjects.length}</span>
              {envFilter ? (
                <span className="project-list-filter ms-auto" style={{ background: envMeta(envFilter).background, color: envMeta(envFilter).color }}>
                  {envMeta(envFilter).short}
                </span>
              ) : canReorder && visibleProjects.length > 1 ? (
                <span className="project-list-hint ms-auto" title="ลากแถวเพื่อจัดลำดับโปรเจค">
                  <i className="fa-solid fa-grip-vertical" /> ลากเพื่อเรียง
                </span>
              ) : null}
            </div>

            <div className="project-list-body">
              {visibleProjects.length ? (
                visibleProjects.map((project) => (
                  <ProjectListItem
                    key={project.uid}
                    project={project}
                    active={project.uid === selectedUid}
                    onClick={() => {
                      setSelectedUid(project.uid);
                      setSelectedEnvKey("");
                    }}
                    draggable={canReorder}
                    dragState={
                      drag.uid === project.uid ? "dragging" : drag.overUid === project.uid ? `over-${drag.position}` : ""
                    }
                    onDragStart={(event) => handleDragStart(event, project.uid)}
                    onDragOver={(event) => handleDragOver(event, project.uid)}
                    onDrop={(event) => handleDrop(event, project.uid)}
                    onDragEnd={handleDragEnd}
                  />
                ))
              ) : (
                <div className="pj-empty py-5">
                  <i className="fa-regular fa-folder-open" />
                  <div>ไม่พบโปรเจคตามเงื่อนไข</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* รายละเอียดโปรเจคที่เลือก */}
        <div className="col-12 col-lg-8 col-xxl-9">
          {selectedProject ? (
            <div className="overflow-hidden" style={sectionCardStyle}>
              <div className="project-hero">
                <div className="project-hero-grid">
                  {/* ซ้าย: ตัวตนของโปรเจค */}
                  <div className="project-hero-main">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {selectedProject.project_code ? <span className="project-code project-code-hero">{selectedProject.project_code}</span> : null}
                      <h3 className="project-hero-title mb-0">{selectedProject.name}</h3>
                      <span className="project-hero-status" style={{ background: statusMeta(selectedProject.status).background, color: statusMeta(selectedProject.status).color }}>
                        <span className="project-hero-status-dot" style={{ background: statusMeta(selectedProject.status).color }} />
                        {statusMeta(selectedProject.status).label}
                      </span>
                    </div>

                    <div className="project-hero-meta">
                      {selectedProject.client_name ? (
                        <span><i className="fa-regular fa-building" />{selectedProject.client_name}</span>
                      ) : null}
                      {selectedProject.owner ? (
                        <span><i className="fa-regular fa-user" />{selectedProject.owner}</span>
                      ) : null}
                      {selectedProject.go_live_date ? (
                        <span><i className="fa-solid fa-rocket" />ขึ้นระบบ {formatDate(selectedProject.go_live_date)}</span>
                      ) : null}
                    </div>

                    {selectedProject.description ? (
                      <div className="project-hero-desc" title={selectedProject.description}>{selectedProject.description}</div>
                    ) : null}
                  </div>

                  {/* ขวา: ลิงก์ · ปุ่มจัดการ วางเรียงแถวเดียวให้หัวการ์ดเตี้ยที่สุด */}
                  <div className="project-hero-side">
                    {selectedProject.doc_url ? (
                      <a className="pj-linkbtn" href={normalizeUrl(selectedProject.doc_url)} target="_blank" rel="noopener noreferrer" title="เปิดเอกสาร / สเปค">
                        <i className="fa-regular fa-file-lines" />เอกสาร
                      </a>
                    ) : null}
                    {selectedProject.board_url ? (
                      <a className="pj-linkbtn" href={normalizeUrl(selectedProject.board_url)} target="_blank" rel="noopener noreferrer" title="เปิดบอร์ดงาน">
                        <i className="fa-solid fa-table-columns" />บอร์ดงาน
                      </a>
                    ) : null}

                    {can("projects.create") ? (
                      <button
                        className="pj-icon-btn pj-icon-btn-accent"
                        onClick={() => setAppModal({ show: true, editData: null, projectUid: selectedProject.uid, projectName: selectedProject.name })}
                      >
                        <i className="fa-solid fa-plus" /><span>เพิ่มแอป</span>
                      </button>
                    ) : null}
                    {can("projects.update") ? (
                      <button className="pj-icon-btn" title="แก้ไขโปรเจค" onClick={() => setProjectModal({ show: true, editData: selectedProject })}>
                        <i className="fa-solid fa-pen" />
                      </button>
                    ) : null}
                    {can("projects.delete") ? (
                      <button className="pj-icon-btn pj-icon-btn-danger" title="ลบโปรเจค" onClick={() => handleDeleteProject(selectedProject)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="p-3 p-md-4">
                <ProjectEndpoints project={selectedProject} onChanged={getData} />

                {(selectedProject.apps || []).length ? (
                  <div className="d-flex flex-column gap-3">
                    {selectedProject.apps.map((app) => {
                      const meta = appMeta(app.app_type);
                      const activeEnv = (app.environments || []).find((env) => `${app.uid}:${env.uid}` === selectedEnvKey);

                      return (
                        <div key={app.uid} className={`app-row ${activeEnv ? "is-open" : ""}`} style={{ "--app-color": meta.color }}>
                          <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                            <div className="d-flex gap-3 align-items-start" style={{ minWidth: 0 }}>
                              <div className="app-row-icon">
                                <i className={`fa-solid ${meta.icon}`} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <span className="app-row-name">{app.name}</span>
                                  <span className="app-row-type">{meta.label}</span>
                                  {app.tech_stack ? <span className="app-row-tech">{app.tech_stack}</span> : null}
                                </div>
                                <div className="app-row-links">
                                  {app.git_url ? (
                                    <ExternalLink url={app.git_url} label={app.git_url.replace(/^https?:\/\/(www\.)?/i, "")} />
                                  ) : (
                                    <span style={{ color: "#a3b6c6" }}>ยังไม่ระบุ Git URL</span>
                                  )}
                                  {app.default_branch ? (
                                    <span className="app-row-branch"><i className="fa-solid fa-code-branch" />{app.default_branch}</span>
                                  ) : null}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex gap-2 flex-shrink-0 align-items-start">
                              {can("projects.update") ? (
                                <button
                                  className="pj-icon-btn"
                                  title="แก้ไขแอป"
                                  onClick={() => setAppModal({ show: true, editData: rawApp(app.uid) || app, projectUid: selectedProject.uid, projectName: selectedProject.name })}
                                >
                                  <i className="fa-solid fa-pen" />
                                </button>
                              ) : null}
                              {can("projects.delete") ? (
                                <button className="pj-icon-btn pj-icon-btn-danger" title="ลบแอป" onClick={() => handleDeleteApp(app)}>
                                  <i className="fa-solid fa-trash" />
                                </button>
                              ) : null}
                            </div>
                          </div>

                          <div className="d-flex flex-wrap gap-2 mt-3">
                            {(app.environments || []).length ? (
                              app.environments.map((environment) => (
                                <EnvChip
                                  key={environment.uid}
                                  environment={environment}
                                  active={`${app.uid}:${environment.uid}` === selectedEnvKey}
                                  onClick={() =>
                                    setSelectedEnvKey(`${app.uid}:${environment.uid}` === selectedEnvKey ? "" : `${app.uid}:${environment.uid}`)
                                  }
                                />
                              ))
                            ) : (
                              <span className="small" style={{ color: "#a3b6c6" }}>ยังไม่มี environment</span>
                            )}
                          </div>

                          {activeEnv ? (
                            <EnvDetail
                              environment={activeEnv}
                              projectName={selectedProject.name}
                              appName={app.name}
                              onOpenEnvFile={(item) => setEnvFileModal({ show: true, environment: item })}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="pj-empty py-5">
                    <i className="fa-solid fa-layer-group" />
                    <div>ยังไม่มีแอปในโปรเจคนี้</div>
                    {can("projects.create") ? (
                      <button
                        className="pj-btn pj-btn-primary mt-3"
                        onClick={() => setAppModal({ show: true, editData: null, projectUid: selectedProject.uid, projectName: selectedProject.name })}
                      >
                        <i className="fa-solid fa-plus me-2" />เพิ่มแอปแรก
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="pj-empty py-5" style={sectionCardStyle}>
              <i className="fa-solid fa-diagram-project" />
              <div>เลือกโปรเจคจากรายการทางซ้าย</div>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          /* ---------- โทนหลักของหน้า ---------- */
          .pj-eyebrow { font-size: .68rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; }

          /* ---------- ปุ่ม ---------- */
          .pj-btn { display: inline-flex; align-items: center; justify-content: center; height: 42px; padding: 0 18px; border-radius: 12px; border: 1px solid transparent; font-size: .875rem; font-weight: 700; white-space: nowrap; transition: transform .14s ease, box-shadow .14s ease, background .14s ease; }
          .pj-btn:active { transform: translateY(1px); }
          .pj-btn-primary { background: linear-gradient(135deg, #0c76e5 0%, #0b4f84 100%); color: #ffffff; box-shadow: 0 10px 22px -12px rgba(12, 118, 229, 0.95); }
          .pj-btn-primary:hover { color: #ffffff; box-shadow: 0 14px 26px -12px rgba(12, 118, 229, 1); transform: translateY(-1px); }
          .pj-btn-ghost { background: #ffffff; border-color: #dbe5ee; color: #4f6d83; }
          .pj-btn-ghost:hover { background: #f4f9ff; border-color: #b9d5f2; color: #0b4f84; }

          .pj-icon-btn { display: inline-flex; align-items: center; gap: 6px; min-width: 36px; height: 36px; padding: 0 11px; border-radius: 10px; border: 1px solid #dbe5ee; background: #ffffff; color: #5b7c96; font-size: .8rem; font-weight: 700; transition: all .14s ease; }
          .pj-icon-btn:hover { border-color: #b9d5f2; background: #f4f9ff; color: #0b4f84; }
          .pj-icon-btn-accent { border-color: rgba(12, 118, 229, 0.24); color: #0c76e5; background: rgba(12, 118, 229, 0.06); }
          .pj-icon-btn-accent:hover { background: rgba(12, 118, 229, 0.12); border-color: rgba(12, 118, 229, 0.42); color: #0b4f84; }
          .pj-icon-btn-danger { border-color: rgba(192, 57, 43, 0.22); color: #c0392b; }
          .pj-icon-btn-danger:hover { background: rgba(192, 57, 43, 0.08); border-color: rgba(192, 57, 43, 0.42); color: #a02a1e; }
          /* ปุ่มไฟล์ ENV อยู่ในแผง .env-detail จึงรับสีจาก --env-color ของ environment ที่เลือก
             ค่า fallback เป็นแดงชุดเดิมสำหรับเบราว์เซอร์ที่ยังไม่รองรับ color-mix */
          .pj-icon-btn-server { border-color: color-mix(in srgb, var(--env-color, #0c76e5) 24%, transparent); color: var(--env-color, #0c76e5); background: color-mix(in srgb, var(--env-color, #0c76e5) 5%, #ffffff); }
          .pj-icon-btn-server:hover { background: color-mix(in srgb, var(--env-color, #0c76e5) 13%, #ffffff); border-color: color-mix(in srgb, var(--env-color, #0c76e5) 45%, transparent); }
          .pj-icon-btn-vault { border-color: rgba(192, 57, 43, 0.24); color: #c0392b; background: rgba(192, 57, 43, 0.05);
            border-color: color-mix(in srgb, var(--env-color) 32%, transparent);
            color: var(--env-color);
            background: color-mix(in srgb, var(--env-color) 7%, #ffffff); }
          .pj-icon-btn-vault:hover { background: rgba(192, 57, 43, 0.10); color: #a02a1e;
            border-color: color-mix(in srgb, var(--env-color) 55%, transparent);
            color: var(--env-color);
            background: color-mix(in srgb, var(--env-color) 15%, #ffffff); }
          .pj-icon-btn-count { display: inline-block; min-width: 20px; padding: 0 6px; border-radius: 999px; background: rgba(192, 57, 43, 0.14); background: color-mix(in srgb, var(--env-color) 18%, transparent); font-size: .7rem; font-variant-numeric: tabular-nums; }

          .pj-linkbtn { display: inline-flex; align-items: center; gap: 7px; height: 32px; padding: 0 13px; border-radius: 999px; background: rgba(255, 255, 255, 0.78); border: 1px solid rgba(12, 118, 229, 0.18); color: #0b4f84; font-size: .8rem; font-weight: 700; text-decoration: none; transition: all .14s ease; }
          .pj-linkbtn:hover { background: #ffffff; border-color: rgba(12, 118, 229, 0.42); color: #0c76e5; transform: translateY(-1px); }

          /* ---------- การ์ดสรุป ---------- */
          .summary-card { position: relative; overflow: hidden; border-radius: 20px; border: 1px solid rgba(15, 60, 120, 0.08); background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%); padding: 16px 18px; box-shadow: 0 1px 2px rgba(16, 46, 82, 0.04), 0 18px 34px -24px rgba(11, 79, 132, 0.45); transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease; }
          .summary-card.is-clickable { cursor: pointer; }
          .summary-card.is-clickable:hover { transform: translateY(-2px); box-shadow: 0 1px 2px rgba(16, 46, 82, 0.04), 0 26px 44px -26px rgba(11, 79, 132, 0.6); }
          .summary-card.is-active { border-color: var(--card-color); box-shadow: 0 0 0 1px var(--card-color), 0 22px 40px -26px var(--card-color); }
          .summary-card-glow { position: absolute; top: -46px; right: -34px; width: 118px; height: 118px; border-radius: 50%; background: var(--card-color); opacity: .07; pointer-events: none; }
          .summary-card-icon { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 42px; height: 42px; border-radius: 13px; font-size: 16px; color: #ffffff; background: var(--card-color); background: linear-gradient(140deg, var(--card-color) 0%, color-mix(in srgb, var(--card-color) 62%, #0b1f3a) 100%); box-shadow: 0 8px 16px -8px var(--card-color); }
          .summary-card-label { margin: 0 0 1px; font-size: .7rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #7b93a8; }
          .summary-card-value { margin: 0; font-size: 1.55rem; font-weight: 800; line-height: 1.1; letter-spacing: -0.02em; color: #11324c; font-variant-numeric: tabular-nums; }
          .summary-card-flag { flex-shrink: 0; align-self: flex-start; font-size: .66rem; font-weight: 800; letter-spacing: .04em; padding: 4px 9px; border-radius: 999px; color: var(--card-color); background: rgba(12, 118, 229, 0.10); background: color-mix(in srgb, var(--card-color) 12%, transparent); }

          /* ---------- แถบเครื่องมือ ---------- */
          .pj-toolbar { display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; align-items: center; padding: 12px 14px; border-radius: 18px; background: rgba(255, 255, 255, 0.86); backdrop-filter: blur(6px); border: 1px solid rgba(15, 60, 120, 0.07); box-shadow: 0 1px 2px rgba(16, 46, 82, 0.03), 0 14px 30px -24px rgba(11, 79, 132, 0.5); }
          .pj-toolbar-fields { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
          .pj-search { position: relative; }
          .pj-search > i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9db4c7; font-size: .82rem; pointer-events: none; }
          .pj-search-input { width: 260px; height: 42px; padding-left: 38px; border-radius: 12px; border: 1px solid #dbe5ee; background: #fbfdff; color: #17364d; font-size: .875rem; box-shadow: none; }
          .pj-search-input:focus { background: #ffffff; border-color: #6fb0f5; box-shadow: 0 0 0 3px rgba(12, 118, 229, 0.12); }
          .pj-field { width: 176px; }
          @media (max-width: 575.98px) {
            .pj-toolbar { align-items: stretch; }
            .pj-toolbar-fields { width: 100%; }
            .pj-search, .pj-search-input, .pj-field { width: 100%; }
            .pj-toolbar > .pj-btn { width: 100%; }
          }

          /* ---------- ช่องกรอกในโมดัล ---------- */
          .pj-input { border-radius: 12px; border: 1px solid #dbe5ee; background: #fbfdff; color: #17364d; font-size: .875rem; padding: 10px 14px; box-shadow: none; transition: border-color .14s ease, box-shadow .14s ease, background .14s ease; }
          .pj-input::placeholder { color: #adbecd; }
          .pj-input:hover { border-color: #c4d6e6; }
          .pj-input:focus, .pj-input:focus-within { background: #ffffff; border-color: #6fb0f5; box-shadow: 0 0 0 3px rgba(12, 118, 229, 0.12); outline: none; }
          textarea.pj-input { line-height: 1.6; }
          /* Select ของโปรเจกต์ render input ซ้อนใน div — คุมความสูงที่ input ชั้นในให้เท่าช่องกรอกปกติ */
          .pj-input > div > input { height: 40px; padding-top: 0 !important; padding-bottom: 0 !important; }
          .pj-input #select-tool { border-radius: 12px !important; border-color: #e0eaf3 !important; padding: 6px !important; box-shadow: 0 18px 38px -18px rgba(11, 79, 132, 0.45); }
          .pj-input #select-tool > div > div { border-radius: 8px; padding: 7px 10px !important; font-size: .85rem; }

          .pj-form-section { margin: 22px 0 12px; font-size: .68rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; color: #8aa4b8; display: flex; align-items: center; gap: 10px; }
          .pj-form-section::after { content: ""; flex: 1; height: 1px; background: linear-gradient(90deg, #e2edf6, transparent); }
          .pj-form-section:first-child { margin-top: 0; }
          .pj-hint { margin-top: 6px; font-size: .78rem; color: #8aa4b8; }
          .pj-callout { display: flex; gap: 10px; align-items: flex-start; padding: 11px 14px; border-radius: 12px; font-size: .8rem; line-height: 1.6; background: rgba(245, 166, 35, 0.10); border: 1px solid rgba(245, 166, 35, 0.24); color: #8a5a05; }
          .pj-callout > i { margin-top: 3px; }

          .pj-modal { border-radius: 22px; box-shadow: 0 40px 90px -30px rgba(8, 40, 75, 0.55); }
          .pj-modal-icon { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 44px; height: 44px; border-radius: 14px; background: rgba(255, 255, 255, 0.16); border: 1px solid rgba(255, 255, 255, 0.20); color: #ffffff; font-size: 17px; }
          .pj-env-tag { display: inline-flex; align-items: center; border-radius: 7px; padding: 3px 8px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .68rem; font-weight: 800; letter-spacing: .06em; }

          /* ---------- รายการโปรเจคฝั่งซ้าย ---------- */
          .project-list-pane { display: flex; flex-direction: column; padding: 8px; }
          .project-list-head { display: flex; align-items: center; gap: 8px; padding: 8px 10px 10px; border-bottom: 1px solid #eef4f9; }
          .project-list-count { min-width: 24px; padding: 1px 8px; border-radius: 999px; background: rgba(12, 118, 229, 0.10); color: #0b4f84; font-size: .72rem; font-weight: 800; font-variant-numeric: tabular-nums; text-align: center; }
          .project-list-filter { padding: 2px 9px; border-radius: 999px; font-size: .68rem; font-weight: 800; letter-spacing: .06em; }
          .project-list-body { overflow-y: auto; max-height: 380px; padding-top: 6px; }
          @media (min-width: 992px) { .project-list-pane { position: sticky; top: 96px; } .project-list-body { max-height: calc(100vh - 340px); } }
          .project-list-body::-webkit-scrollbar { width: 6px; }
          .project-list-body::-webkit-scrollbar-thumb { background: #d8e6f2; border-radius: 999px; }
          .project-list-body::-webkit-scrollbar-track { background: transparent; }

          .project-list-hint { display: inline-flex; align-items: center; gap: 5px; font-size: .68rem; font-weight: 600; color: #a9bfd0; }
          .project-row-wrap { position: relative; border-radius: 14px; transition: opacity .14s ease, box-shadow .14s ease, transform .14s ease; }
          .project-row-wrap[draggable="true"] { cursor: grab; }
          .project-row-wrap[draggable="true"]:active { cursor: grabbing; }
          .project-row-wrap.is-dragging { opacity: .35; }
          .project-row-wrap.is-over-before::before, .project-row-wrap.is-over-after::after { content: ""; position: absolute; left: 10px; right: 10px; height: 3px; border-radius: 999px; background: #0c76e5; box-shadow: 0 0 0 3px rgba(12, 118, 229, 0.16); z-index: 2; pointer-events: none; }
          .project-row-wrap.is-over-before::before { top: -2px; }
          .project-row-wrap.is-over-after::after { bottom: -1px; }
          .project-row-grip { position: absolute; left: -2px; top: 50%; transform: translateY(-50%); z-index: 1; display: flex; align-items: center; justify-content: center; width: 14px; height: 26px; color: #c2d2df; font-size: .7rem; opacity: 0; transition: opacity .14s ease, color .14s ease; pointer-events: none; }
          .project-row-wrap:hover .project-row-grip { opacity: 1; }
          .project-row-wrap:hover .project-row-grip, .project-row-wrap.is-dragging .project-row-grip { color: #7b93a8; }
          .project-row-wrap[draggable="true"] .project-row { padding-left: 18px; }
          .project-row-wrap[draggable="true"] .project-row-rail { left: 10px; }
          .project-row { position: relative; display: block; width: 100%; text-align: left; border: 1px solid transparent; background: transparent; border-radius: 14px; padding: 11px 12px 11px 14px; margin-bottom: 2px; transition: background .14s ease, border-color .14s ease; }
          .project-row:hover { background: #f5faff; }
          .project-row.active { background: linear-gradient(90deg, #eaf4ff 0%, #f6fbff 100%); border-color: rgba(12, 117, 230, 0.20); }
          .project-row-rail { position: absolute; left: 4px; top: 12px; bottom: 12px; width: 3px; border-radius: 999px; opacity: 0; transition: opacity .14s ease; }
          .project-row.active .project-row-rail { opacity: 1; }
          .project-row-name { color: #11324c; font-weight: 600; font-size: .9rem; }
          .project-row.active .project-row-name { font-weight: 800; }
          .project-row-status { width: 8px; height: 8px; border-radius: 50%; }
          .project-row-meta { display: flex; align-items: center; gap: 7px; margin-top: 5px; font-size: .75rem; color: #8aa4b8; }
          .project-row-meta b { color: #5b7c96; font-weight: 700; font-variant-numeric: tabular-nums; }
          .project-row-sep { width: 3px; height: 3px; border-radius: 50%; background: #cddceb; }
          .project-row-envdot { width: 6px; height: 6px; border-radius: 50%; }

          .project-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .7rem; font-weight: 800; letter-spacing: .04em; color: #0b4f84; background: rgba(12, 117, 230, 0.10); border-radius: 7px; padding: 2px 8px; flex-shrink: 0; }
          .project-code-hero { font-size: .76rem; background: rgba(12, 117, 230, 0.12); padding: 3px 10px; }

          /* ---------- หัวโปรเจคที่เลือก ---------- */
          .project-hero { padding: 14px 16px; background: radial-gradient(circle at 96% -40%, rgba(47, 213, 228, 0.16), transparent 42%), linear-gradient(180deg, #f3f9ff 0%, #ffffff 100%); border-bottom: 1px solid #eef4f9; }
          @media (min-width: 768px) { .project-hero { padding: 16px 22px; } }
          .project-hero-title { font-size: 1.24rem; font-weight: 800; letter-spacing: -0.02em; color: #0f2942; }
          .project-hero-status { display: inline-flex; align-items: center; gap: 7px; border-radius: 999px; padding: 4px 13px; font-size: .76rem; font-weight: 800; }
          .project-hero-status-dot { width: 6px; height: 6px; border-radius: 50%; }
          .project-hero-meta { display: flex; flex-wrap: wrap; gap: 2px 18px; margin-top: 5px; font-size: .8rem; line-height: 1.5; color: #5b7c96; }
          .project-hero-meta > span { display: inline-flex; align-items: center; gap: 7px; }
          .project-hero-meta i { color: #9db4c7; font-size: .78rem; width: 13px; text-align: center; }
          .project-hero-desc { margin-top: 3px; max-width: 620px; font-size: .8rem; line-height: 1.5; color: #8aa4b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

          /* หัวการ์ด: ซ้ายเป็นตัวตนของโปรเจค ขวาเป็นตัวเลข/ลิงก์/ปุ่ม อยู่ในแถวเดียวกันทั้งหมด */
          .project-hero-grid { display: flex; flex-direction: column; gap: 10px; }
          .project-hero-main { min-width: 0; }
          .project-hero-side { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; flex-shrink: 0; }
          @media (min-width: 992px) {
            .project-hero-grid { flex-direction: row; justify-content: space-between; align-items: center; gap: 20px; }
            .project-hero-side { justify-content: flex-end; }
          }

          .project-hero-side .pj-linkbtn { height: 36px; }

          /* ---------- แถวแอป ---------- */
          .app-row { position: relative; border: 1px solid rgba(15, 60, 120, 0.08); border-radius: 18px; padding: 15px 16px; background: #ffffff; box-shadow: 0 1px 2px rgba(16, 46, 82, 0.03); transition: box-shadow .16s ease, border-color .16s ease, transform .16s ease; }
          .app-row:hover { border-color: rgba(12, 118, 229, 0.20); box-shadow: 0 1px 2px rgba(16, 46, 82, 0.04), 0 18px 34px -26px rgba(11, 79, 132, 0.55); }
          .app-row.is-open { border-color: rgba(12, 118, 229, 0.24); box-shadow: 0 1px 2px rgba(16, 46, 82, 0.04), 0 22px 40px -28px rgba(11, 79, 132, 0.7); }
          .app-row-icon { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 38px; height: 38px; border-radius: 12px; font-size: 15px; color: #ffffff; background: var(--app-color); background: linear-gradient(140deg, var(--app-color) 0%, color-mix(in srgb, var(--app-color) 60%, #0b1f3a) 100%); box-shadow: 0 8px 16px -9px var(--app-color); }
          .app-row-name { font-size: .95rem; font-weight: 800; letter-spacing: -0.01em; color: #0f2942; }
          .app-row-type { border-radius: 999px; padding: 2px 9px; font-size: .68rem; font-weight: 800; letter-spacing: .03em; color: var(--app-color); background: rgba(12, 118, 229, 0.10); background: color-mix(in srgb, var(--app-color) 11%, transparent); }
          .app-row-tech { font-size: .76rem; color: #8aa4b8; }
          .app-row-links { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; margin-top: 6px; font-size: .8rem; }
          .app-row-branch { display: inline-flex; align-items: center; gap: 6px; color: #7b93a8; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .74rem; }
          .app-row-branch i { color: #a9bfd0; }
          .env-ip-list { display: flex; flex-direction: column; gap: 8px; }
          .env-ip-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 12px; background: #ffffff; border: 1px solid color-mix(in srgb, var(--env-color, #0c76e5) 16%, #e6eef5); border-left: 3px solid var(--env-color, #0c76e5); box-shadow: 0 8px 18px -14px color-mix(in srgb, var(--env-color, #0c76e5) 60%, transparent); transition: background .12s ease, border-color .12s ease; }
          .env-ip-item:hover { background: color-mix(in srgb, var(--env-color, #0c76e5) 5%, #ffffff); border-color: color-mix(in srgb, var(--env-color, #0c76e5) 32%, #e6eef5); }
          .env-ip-index { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 24px; height: 24px; border-radius: 8px; font-size: .7rem; font-weight: 800; color: var(--env-color, #0c76e5); background: color-mix(in srgb, var(--env-color, #0c76e5) 12%, transparent); }
          .env-ip-value { flex: 1; min-width: 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .92rem; font-weight: 700; color: #0f2942; word-break: break-all; }
          .env-ip-item .pj-icon-btn { border-color: color-mix(in srgb, var(--env-color, #0c76e5) 24%, transparent); color: var(--env-color, #0c76e5); background: color-mix(in srgb, var(--env-color, #0c76e5) 5%, #ffffff); }
          .env-ip-item .pj-icon-btn:hover { background: color-mix(in srgb, var(--env-color, #0c76e5) 13%, #ffffff); border-color: color-mix(in srgb, var(--env-color, #0c76e5) 45%, transparent); }
          .pj-btn-env { background: linear-gradient(135deg, var(--env-color, #0c76e5) 0%, color-mix(in srgb, var(--env-color, #0c76e5) 70%, #0b1f33) 100%); color: #ffffff; box-shadow: 0 10px 22px -12px color-mix(in srgb, var(--env-color, #0c76e5) 95%, transparent); }
          .pj-btn-env:hover { color: #ffffff; filter: brightness(1.06); }

          /* ---------- ชิป environment ---------- */
          .env-chip { display: inline-flex; align-items: center; gap: 7px; border: 1px solid transparent; border-radius: 999px; padding: 6px 13px; font-size: .78rem; font-weight: 700; transition: transform .14s ease, box-shadow .14s ease; }
          .env-chip:hover { transform: translateY(-1px); }
          .env-chip-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
          .env-chip-count { display: inline-flex; align-items: center; gap: 4px; border-radius: 999px; padding: 1px 8px; font-size: .7rem; font-weight: 800; font-variant-numeric: tabular-nums; }
          .env-chip-warn { color: #f5a623; }
          .env-chip.is-active .env-chip-warn { color: #ffffff; }

          /* ---------- รายละเอียด environment ---------- */
          /* พื้นหลังของแผงย่อยไล่โทนตามสีของ environment ที่เลือก (PROD แดง / DEV ฟ้า ...)
             ประกาศสีทึบไว้ก่อนทุกครั้งเป็น fallback ให้เบราว์เซอร์ที่ยังไม่รองรับ color-mix */
          .env-detail { position: relative; margin-top: 14px; padding: 16px 16px 16px 20px; border-radius: 16px; overflow: hidden;
            background: #f8fbff;
            background: radial-gradient(circle at 98% -60%, color-mix(in srgb, var(--env-color) 14%, transparent), transparent 46%), linear-gradient(180deg, color-mix(in srgb, var(--env-color) 4%, #ffffff) 0%, color-mix(in srgb, var(--env-color) 9%, #ffffff) 100%);
            border: 1px solid rgba(15, 60, 120, 0.07);
            border-color: color-mix(in srgb, var(--env-color) 20%, transparent); }
          .env-detail-bar { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; }
          .env-group { padding: 12px 14px; border-radius: 13px; background: rgba(255, 255, 255, 0.82); border: 1px solid rgba(15, 60, 120, 0.06); border-color: color-mix(in srgb, var(--env-color) 12%, transparent); }
          .env-group-title { display: flex; align-items: center; gap: 7px; margin-bottom: 9px; padding-bottom: 8px; border-bottom: 1px dashed #e6eff7; border-bottom-color: color-mix(in srgb, var(--env-color) 16%, transparent); font-size: .67rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: #93a9bc; }
          .env-group-title i { color: var(--env-color); font-size: .74rem; }
          /* ---------- แผงปลายทางระดับโปรเจค ---------- */
          .endpoint-board { margin-bottom: 16px; padding: 14px 16px; border-radius: 18px; background: linear-gradient(180deg, #fbfdff 0%, #f4f9ff 100%); border: 1px solid rgba(15, 60, 120, 0.08); }
          .endpoint-board-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
          .endpoint-board-title { display: flex; align-items: center; gap: 8px; font-size: .7rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; color: #7b93a8; }
          .endpoint-board-title i { color: var(--env-color); font-size: .82rem; }
          .endpoint-board-empty { text-align: center; }
          .endpoint-board-empty .endpoint-board-title { justify-content: center; color: #9db4c7; }
          .endpoint-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
          .endpoint-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
          .endpoint-tab { display: inline-flex; align-items: center; gap: 7px; height: 30px; padding: 0 13px; border: 1px solid transparent; border-radius: 999px; font-size: .74rem; font-weight: 800; letter-spacing: .04em; transition: transform .12s ease, box-shadow .12s ease, opacity .12s ease; }
          .endpoint-tab:hover { transform: translateY(-1px); }
          .endpoint-tab:disabled { transform: none; cursor: default; }
          .endpoint-tab.is-active { box-shadow: 0 8px 16px -9px currentColor; }
          .endpoint-tab-add { padding: 0 11px; border-color: #dbe5ee; background: #ffffff; color: #7b93a8; }
          .endpoint-tab-add:hover { border-color: #b9d5f2; color: #0c76e5; }
          .endpoint-actions { display: flex; align-items: center; gap: 6px; margin-left: 4px; padding-left: 10px; border-left: 1px solid #e2edf6; }

          .env-rows { display: flex; flex-direction: column; gap: 8px; }
          .env-block { display: flex; flex-direction: column; gap: 8px; padding: 10px 12px; border-radius: 14px; border: 1px solid rgba(15, 60, 120, 0.08); border-left: 3px solid var(--env-color, #0c76e5); background: #fbfdff; }
          .env-block-ips { display: flex; align-items: flex-start; gap: 10px; }
          .env-block-ips-label { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; width: 96px; padding-top: 8px; font-size: .66rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #93a9bc; }
          .env-block-ips-label i { color: var(--env-color, #0c76e5); font-size: .74rem; }
          .env-block-ips-list { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 0; }
          .env-block-ip-row { display: flex; align-items: center; gap: 6px; }
          .env-row { display: flex; align-items: center; gap: 8px; }
          .env-row-type { width: 190px; flex-shrink: 0; }
          @media (max-width: 575.98px) {
            .env-row { flex-wrap: wrap; }
            .env-block-ips { flex-direction: column; gap: 4px; }
            .env-block-ips-label { width: auto; padding-top: 0; }
            .env-row-type { width: calc(100% - 44px); }
          }

          .endpoint-form { display: flex; flex-direction: column; gap: 8px; }
          .endpoint-form-row { display: flex; align-items: center; gap: 10px; }
          .endpoint-form-label { display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0; width: 108px; font-size: .7rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #7b93a8; }
          .endpoint-form-label i { width: 14px; text-align: center; color: var(--env-color); font-size: .78rem; }
          @media (max-width: 575.98px) {
            .endpoint-form-row { flex-direction: column; align-items: stretch; gap: 4px; }
            .endpoint-form-label { width: auto; }
          }

          .env-endpoints { display: flex; flex-direction: column; }
          .env-endpoint { display: flex; align-items: center; gap: 10px; padding: 8px 8px 8px 6px; margin: 0 -6px; border-radius: 10px; transition: background .14s ease; }
          .env-endpoint + .env-endpoint { box-shadow: inset 0 1px 0 #f0f6fb; }
          .env-endpoint:hover { background: #f6fbff; }
          .env-endpoint.is-empty:hover { background: transparent; }
          .env-endpoint-icon { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 26px; height: 26px; border-radius: 8px; font-size: .72rem; color: var(--env-color); background: #f2f7fb; background: color-mix(in srgb, var(--env-color) 11%, transparent); }
          .env-endpoint.is-empty .env-endpoint-icon { color: #b7c8d6; background: #f2f7fb; }
          .env-endpoint-label { flex-shrink: 0; width: 46px; font-size: .68rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #9db4c7; }
          .env-endpoint-url { flex: 1; min-width: 0; font-size: .84rem; font-weight: 600; color: #0f2942; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .env-endpoint:hover .env-endpoint-url { color: #0c76e5; text-decoration: underline; text-underline-offset: 3px; }
          .env-endpoint-dash { flex: 1; min-width: 0; font-size: .84rem; color: #c2d2df; }
          .env-endpoint-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
          .env-endpoint-actions > button, .env-endpoint-actions > a { display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border: 0; border-radius: 8px; background: transparent; color: #a9bfd0; font-size: .76rem; text-decoration: none; transition: background .12s ease, color .12s ease; }
          .env-endpoint:hover .env-endpoint-actions > button, .env-endpoint:hover .env-endpoint-actions > a { color: #7b93a8; }
          .env-endpoint-actions > button:hover, .env-endpoint-actions > a:hover { background: rgba(12, 118, 229, 0.10); color: #0c76e5; }

          .env-kv { display: flex; gap: 10px; align-items: baseline; padding: 3px 0; font-size: .82rem; line-height: 1.7; }
          .env-kv-label { flex-shrink: 0; min-width: 58px; color: #9db4c7; font-size: .76rem; }
          .env-kv-value { color: #1b3950; word-break: break-word; min-width: 0; }
          .pj-expiry-pill { border-radius: 6px; padding: 1px 7px; font-size: .7rem; font-weight: 700; }

          /* ---------- สถานะว่าง ---------- */
          .pj-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; color: #9db4c7; font-size: .86rem; }
          .pj-empty > i { font-size: 26px; margin-bottom: 12px; color: #cddceb; }

          /* ---------- โมดัลไฟล์ ENV ---------- */
          .pj-btn-danger { background: #ffffff; border-color: rgba(192, 57, 43, 0.30); color: #c0392b; }
          .pj-btn-danger:hover { background: rgba(192, 57, 43, 0.07); border-color: rgba(192, 57, 43, 0.5); color: #a02a1e; }
          .pj-icon-btn-lock { border-color: rgba(22, 111, 71, 0.24); color: #166f47; background: rgba(255, 255, 255, 0.7); }
          .pj-icon-btn-lock:hover { background: #ffffff; border-color: rgba(22, 111, 71, 0.45); color: #0f5233; }

          .pj-callout-danger { background: rgba(192, 57, 43, 0.07); border-color: rgba(192, 57, 43, 0.20); color: #a02a1e; }

          .pj-panel { padding: 18px 20px; border-radius: 16px; background: #ffffff; border: 1px solid rgba(15, 60, 120, 0.08); box-shadow: 0 1px 2px rgba(16, 46, 82, 0.03); }
          .pj-panel-danger { border-color: rgba(192, 57, 43, 0.22); }
          .pj-panel-title { display: flex; align-items: center; font-size: 1rem; font-weight: 800; letter-spacing: -0.01em; color: #11324c; }

          .pj-unlock-bar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; padding: 10px 14px; border-radius: 13px; background: rgba(47, 181, 114, 0.10); border: 1px solid rgba(47, 181, 114, 0.24); color: #166f47; font-size: .84rem; }
          .pj-unlock-timer { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .8rem; font-weight: 800; padding: 2px 9px; border-radius: 999px; background: rgba(22, 111, 71, 0.14); font-variant-numeric: tabular-nums; }

          .pj-stat-row { display: flex; flex-wrap: wrap; gap: 10px 34px; }
          .pj-stat { display: flex; flex-direction: column; gap: 2px; }
          .pj-stat-label { font-size: .66rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: #9db4c7; }
          .pj-stat-value { font-size: 1rem; font-weight: 800; color: #11324c; font-variant-numeric: tabular-nums; }
          .pj-stat-value small { font-size: .74rem; font-weight: 600; color: #8aa4b8; }

          .pj-key-chip { border-radius: 8px; padding: 3px 9px; background: #f2f8fd; border: 1px solid #e4eef7; color: #0b4f84; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .73rem; font-weight: 600; }
          .pj-env-editor { border-radius: 14px !important; background: #fbfdff; }
          .pj-dirty { display: inline-flex; align-items: center; gap: 7px; font-size: .78rem; font-weight: 700; color: #b97007; }
          .pj-dirty-dot { width: 7px; height: 7px; border-radius: 50%; background: #f5a623; }

          .pj-recovery-code { border-radius: 10px; padding: 8px 12px; text-align: center; background: #f4f8fc; border: 1px solid #e4eef7; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .82rem; font-weight: 800; color: #11324c; }
          .pj-qr { padding: 14px; border-radius: 16px; background: #ffffff; border: 1px solid #e3edf6; box-shadow: 0 1px 2px rgba(16, 46, 82, 0.04); line-height: 0; }
          .pj-secret { display: inline-block; border-radius: 10px; padding: 8px 13px; background: #f4f8fc; border: 1px solid #e4eef7; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .82rem; font-weight: 700; color: #11324c; }
        `}
      </style>

      <ProjectModal
        show={projectModal.show}
        editData={projectModal.editData}
        onHide={() => setProjectModal({ show: false, editData: null })}
        onSave={handleSaveProject}
      />

      <AppModal
        show={appModal.show}
        editData={appModal.editData}
        projectUid={appModal.projectUid}
        projectName={appModal.projectName}
        onHide={() => setAppModal({ show: false, editData: null, projectUid: "", projectName: "" })}
        onSave={handleSaveApp}
      />

      <EnvFileModal
        show={envFileModal.show}
        environment={envFileModal.environment}
        onHide={() => setEnvFileModal({ show: false, environment: null })}
        onChanged={getData}
      />
    </AppShell>
  );
}
